import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { alpha, Stack, CircularProgress, Slide, useTheme, DialogTitle, Box, IconButton as IconButton$1, useMediaQuery, Typography as Typography$1, DialogContent, DialogActions, Button as Button$1, Dialog as Dialog$2, Container, createTheme } from "@mui/material";
import { InlineWidget, PopupModal } from "react-calendly";
import { useLocation, Link as Link$1 } from "react-router-dom";
import * as React from "react";
import React__default, { useRef, useCallback, useEffect, useState } from "react";
import { Close as Close$1 } from "@mui/icons-material";
const BRAND_KEYS = {
  ATHENA: "athena",
  MORTGAGE_CHOICE: "mortgageChoice",
  LMG: "lmg",
  DRAFT_VIEWER: "draftViewer"
};
const getAthenaLightPalette = () => ({
  palette: {
    mode: "light",
    primary: {
      main: "#3B015F",
      contrastText: "#ffffff",
      light: "#E8D6F7",
      dark: "#210234"
    },
    text: {
      primary: "#000000",
      secondary: "#555159"
    },
    secondary: {
      main: "#6A1B9A"
    },
    tertiary: {
      main: "#3B015F",
      contrastText: "#ffffff",
      light: "#E8D6F7",
      dark: "#210234"
    },
    warning: {
      main: "#ff7100",
      contrastText: "#ffffff"
    },
    info: {
      contrastText: "#ffffff",
      main: "#7200CB",
      dark: "#4F008E"
    },
    success: {
      main: "#4CAF50",
      contrastText: "#ffffff"
    },
    error: {
      contrastText: "#ffffff",
      main: "#F44336"
    },
    background: {
      default: "#FAFAF8"
    }
  }
});
const getDraftViewerLightPalette = () => ({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
      contrastText: "#ffffff",
      light: "#E6F1FC",
      dark: "#272c4a"
    },
    text: {
      primary: "#000000",
      secondary: "#2A2630"
    },
    secondary: {
      main: "#5c5965"
    },
    tertiary: {
      main: "#de026a",
      contrastText: "#ffffff",
      light: "#E6F1FC",
      dark: "#272c4a"
    },
    warning: {
      contrastText: "#ffffff",
      main: "#ff7100"
    },
    info: {
      contrastText: "#ffffff",
      main: "#7200CB",
      dark: "#4F008E"
    },
    success: {
      contrastText: "#ffffff",
      main: "#4CAF50"
    },
    error: {
      contrastText: "#ffffff",
      main: "#F44336"
    },
    background: {
      default: "#FAFAF8"
    }
  }
});
const getLmgLightDesignTokens = () => ({
  palette: {
    mode: "light",
    primary: {
      main: "#D2295C",
      contrastText: "#ffffff",
      light: "#FFECEE",
      dark: "#9D1E43"
    },
    text: {
      primary: "#3D3A40",
      secondary: "#3D3A40"
    },
    secondary: {
      main: "#203440"
    },
    tertiary: {
      main: "#D2295C",
      contrastText: "#ffffff",
      light: "#FFECEE",
      dark: "#9D1E43"
    },
    warning: {
      contrastText: "#ffffff",
      main: "#ff7100"
    },
    info: {
      contrastText: "#ffffff",
      main: "#2196f3",
      dark: "#1769AA"
    },
    success: {
      contrastText: "#ffffff",
      main: "#4CAF50"
    },
    error: {
      contrastText: "#ffffff",
      main: "#F44336"
    },
    background: {
      default: "#FBF7F4"
    }
  },
  typography: {
    allVariants: {
      color: "#3D3A40"
    }
  }
});
const getMortgageChoiceLightDesignTokens = () => ({
  palette: {
    mode: "light",
    primary: {
      main: "#006875",
      contrastText: "#ffffff",
      light: "#E6F0F1",
      dark: "#004A53"
    },
    text: {
      primary: "#3D3A40",
      secondary: "#3D3A40"
    },
    secondary: {
      main: "#17151A"
    },
    tertiary: {
      main: "#006875",
      contrastText: "#ffffff",
      light: "#E6F0F1",
      dark: "#004A53"
    },
    warning: {
      contrastText: "#ffffff",
      main: "#ff7100"
    },
    info: {
      contrastText: "#ffffff",
      main: "#40A09F",
      dark: "#2C706F"
    },
    success: {
      contrastText: "#ffffff",
      main: "#4CAF50"
    },
    error: {
      contrastText: "#ffffff",
      main: "#F44336"
    },
    background: {
      default: "#F6F5F7"
    }
  },
  typography: {
    allVariants: {
      color: "#3D3A40"
    }
  }
});
const getTheme = ({ activeBrand }) => {
  const themeColors = {
    [BRAND_KEYS.ATHENA]: getAthenaLightPalette,
    [BRAND_KEYS.DRAFT_VIEWER]: getDraftViewerLightPalette,
    [BRAND_KEYS.LMG]: getLmgLightDesignTokens,
    [BRAND_KEYS.MORTGAGE_CHOICE]: getMortgageChoiceLightDesignTokens
  };
  return themeColors[activeBrand]();
};
const convertThemeToStyle = ({ palette }) => ({
  // All Palette Colors
  "--nugs-primary-main": palette.primary.main,
  "--nugs-primary-main-010": alpha(palette.primary.main, 0.1),
  "--nugs-primary-contrastText": palette.primary.contrastText,
  "--nugs-primary-light": palette.primary.light,
  "--nugs-primary-dark": palette.primary.dark,
  "--nugs-text-primary": palette.text.primary,
  "--nugs-text-secondary": palette.text.secondary,
  "--nugs-secondary-main": palette.secondary.main,
  "--nugs-tertiary-main": palette.tertiary.main,
  "--nugs-tertiary-contrastText": palette.tertiary.contrastText,
  "--nugs-tertiary-light": palette.tertiary.light,
  "--nugs-tertiary-dark": palette.tertiary.dark,
  "--nugs-warning-main": palette.warning.main,
  "--nugs-warning-contrastText": palette.warning.contrastText,
  "--nugs-info-contrastText": palette.info.contrastText,
  "--nugs-info-main": palette.info.main,
  "--nugs-info-dark": palette.info.dark,
  "--nugs-success-main": palette.success.main,
  "--nugs-success-contrastText": palette.success.contrastText,
  "--nugs-error-contrastText": palette.error.contrastText,
  "--nugs-error-main": palette.error.main,
  "--nugs-background-default": palette.background.default,
  "--nugs-grey-100": "#f5f5f5",
  "--nugs-grey-500": "#9e9e9e",
  "--nugs-grey-600": "#757575",
  // Component Specific Colors
  "--nugs-button-primary": palette.primary.main,
  "--nugs-divider-color": "var(--nugs-grey-500)",
  "--nugs-footer-text": palette.text.secondary,
  "--nugs-footer-background": palette.background.default,
  "--nugs-icon-button-background": alpha(palette.primary.main, 0.1),
  "--nugs-link-primary": palette.primary.main,
  "--nugs-dialog-button-base": alpha(palette.primary.main, 0.05),
  "--nugs-dialog-button-active": alpha(palette.primary.main, 0.1),
  "--nugs-input-border": "#d8d8d8",
  "--nugs-input-placeholder": "#c5c5c5",
  "--nugs-snack-bar-info-background": "rgba(0, 0, 0, 0.75)",
  "--nugs-snack-bar-info-text": "#FFFFFF",
  "--nugs-snack-bar-error-background": "#D70015",
  "--nugs-snack-bar-error-text": "#FFFFFF"
});
const cssVar = {
  primaryMain: "var(--nugs-primary-main)",
  primaryMain0_1: "var(--nugs-primary-main-010)",
  primaryContrastText: "var(--nugs-primary-contrastText)",
  primaryLight: "var(--nugs-primary-light)",
  primaryDark: "var(--nugs-primary-dark)",
  textPrimary: "var(--nugs-text-primary)",
  textSecondary: "var(--nugs-text-secondary)",
  secondaryMain: "var(--nugs-secondary-main)",
  tertiaryMain: "var(--nugs-tertiary-main)",
  tertiaryContrastText: "var(--nugs-tertiary-contrastText)",
  tertiaryLight: "var(--nugs-tertiary-light)",
  tertiaryDark: "var(--nugs-tertiary-dark)",
  warningMain: "var(--nugs-warning-main)",
  warningContrastText: "var(--nugs-warning-contrastText)",
  infoContrastText: "var(--nugs-info-contrastText)",
  infoMain: "var(--nugs-info-main)",
  infoDark: "var(--nugs-info-dark)",
  successMain: "var(--nugs-success-main)",
  successContrastText: "var(--nugs-success-contrastText)",
  errorContrastText: "var(--nugs-error-contrastText)",
  errorMain: "var(--nugs-error-main)",
  backgroundDefault: "var(--nugs-background-default)",
  grey100: "var(--nugs-grey-100)",
  grey500: "var(--nugs-grey-500)",
  grey600: "var(--nugs-grey-600)",
  // Component specific colors
  buttonPrimary: "var(--nugs-button-primary)",
  footerText: "var(--nugs-footer-text)",
  footerBackground: "var(--nugs-footer-background)",
  dividerColor: "var(--nugs-divider-color)",
  iconButtonBackground: "var(--nugs-icon-button-background)",
  linkPrimary: "var(--nugs-link-primary)",
  dialogButtonBase: "var(--nugs-dialog-button-base)",
  dialogButtonActive: "var(--nugs-dialog-button-active)",
  inputBorder: "var(--nugs-input-border)",
  inputPlaceholder: "var(--nugs-input-placeholder)",
  snackBarInfoBackground: "var(--nugs-snack-bar-info-background)",
  snackBarInfoText: "var(--nugs-snack-bar-info-text)",
  snackBarErrorBackground: "var(--nugs-snack-bar-error-background)",
  snackBarErrorText: "var(--nugs-snack-bar-error-text)"
};
const getThemeStyles = ({ activeBrand }) => {
  const theme = getTheme({ activeBrand });
  return convertThemeToStyle(theme);
};
const Button = ({ children, variant = "contained", style: overrideStyle, ...rest }) => {
  const style = {};
  switch (variant) {
    case "contained":
      style.backgroundColor = cssVar.primaryMain;
      style.fill = cssVar.primaryContrastText;
      style.color = cssVar.primaryContrastText;
      break;
    case "outlined":
      style.backgroundColor = "transparent";
      style.color = cssVar.primaryMain;
      style.border = `1px solid ${cssVar.primaryMain}`;
      break;
    case "text":
      style.backgroundColor = "transparent";
      style.color = cssVar.primaryMain;
      style.boxShadow = "none";
      break;
  }
  const styleFocused = (e) => {
    e.currentTarget.style.backgroundColor = cssVar.primaryDark;
  };
  const styleBlurred = (e) => {
    e.currentTarget.style.backgroundColor = cssVar.primaryMain;
  };
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      style: {
        appearance: "none",
        alignItems: "center",
        border: "none",
        borderRadius: 5,
        cursor: "pointer",
        display: "inline-flex",
        justifyContent: "center",
        padding: "8px 16px",
        transition: "0.3s",
        fontFamily: "system-ui",
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.5,
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
        gap: 8,
        ...style,
        ...overrideStyle
      },
      onMouseEnter: styleFocused,
      onMouseLeave: styleBlurred,
      onFocus: styleFocused,
      onBlur: styleBlurred,
      ...rest,
      children
    }
  );
};
function LoadingScreen({ height = "100vh" }) {
  return /* @__PURE__ */ jsx(Stack, { justifyContent: "center", alignItems: "center", sx: { width: "100%", height }, children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": "Loading..." }) });
}
function useRefWithCallback(onMount, onUnmount) {
  const nodeRef = useRef(null);
  const setRef = useCallback(
    (node) => {
      if (nodeRef.current !== null) {
        onUnmount(nodeRef.current);
      }
      nodeRef.current = node;
      if (nodeRef.current !== null) {
        onMount(nodeRef.current);
      }
    },
    [onMount, onUnmount]
  );
  return setRef;
}
const responsiveDialogSpacing = {
  xs: 3,
  md: 6
};
const Transition = React__default.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(Slide, { direction: "up", ref, ...props })
);
function DialogControl(props) {
  const theme = useTheme();
  const { children, handleClose, id, open, scrollBody, matches } = props;
  const spacing = theme.spacing(4);
  const displayFullScreenModal = matches === true || scrollBody;
  const scrollBodyStyles = {
    pt: 5,
    maxWidth: "100%",
    m: `${spacing} 0px 0px 0px`,
    height: matches === true ? "100%" : "auto",
    borderRadius: `${spacing} ${spacing} 0px 0px`,
    width: displayFullScreenModal ? "100%" : "60%"
  };
  const dialogStyles = {
    py: 5,
    maxWidth: "788px",
    borderRadius: spacing,
    height: matches === true ? "100%" : "auto",
    m: `${spacing} 0 ${theme.spacing(5)}`,
    width: displayFullScreenModal ? "100%" : "60%",
    ...props.sx
  };
  return /* @__PURE__ */ jsx(
    Dialog$2,
    {
      open,
      scroll: "body",
      onClose: handleClose,
      "aria-labelledby": `${id}-title`,
      TransitionComponent: Transition,
      "aria-describedby": `${id}-description`,
      sx: {
        "& > .MuiDialog-container > .MuiDialog-paperScrollBody": {
          ...displayFullScreenModal ? scrollBodyStyles : dialogStyles
        }
      },
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          sx: {
            minHeight: displayFullScreenModal ? "calc(100vh - 104px)" : "auto",
            width: "100%"
          },
          children: [
            scrollBody ? /* @__PURE__ */ jsx(Box, { sx: { position: "absolute", right: [50, null, 56], top: "10px", zIndex: 2 }, children: /* @__PURE__ */ jsx(
              IconButton$1,
              {
                "aria-label": "close",
                onClick: handleClose,
                sx: {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  color: "primary.main",
                  height: [40, 40, 48],
                  position: "fixed",
                  transition: "background 300ms ease-in-out",
                  width: [40, 40, 48],
                  zIndex: 1,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }
                },
                children: /* @__PURE__ */ jsx(Close$1, {})
              }
            ) }) : null,
            /* @__PURE__ */ jsx("div", { children })
          ]
        }
      )
    }
  );
}
const WrappedDialogTitle = React__default.forwardRef((props, ref) => {
  const theme = useTheme();
  const { children, handleClose, scrollBody, matches = false, hideCloseIcon, ...other } = props;
  const right = matches ? "48px" : "56px";
  const top = matches ? "8px" : "8px";
  return /* @__PURE__ */ jsxs(
    DialogTitle,
    {
      ref,
      sx: {
        m: 0,
        px: { ...responsiveDialogSpacing }
      },
      ...other,
      children: [
        scrollBody === false && handleClose != null && hideCloseIcon === false ? /* @__PURE__ */ jsx(Box, { sx: { position: "absolute", right, top, zIndex: 2 }, children: /* @__PURE__ */ jsx(
          IconButton$1,
          {
            "aria-label": "close",
            onClick: handleClose,
            sx: {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              color: "primary.main",
              height: [40, 40, 48],
              position: "fixed",
              transition: "background 300ms ease-in-out",
              width: [40, 40, 48],
              zIndex: 1,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            },
            children: /* @__PURE__ */ jsx(Close$1, {})
          }
        ) }) : null,
        /* @__PURE__ */ jsx(Box, { sx: { position: "relative", zIndex: 1 }, children })
      ]
    }
  );
});
function GradientComponent({ top = "unset", bottom = "unset", showGradient }) {
  const topGradient = "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)";
  const bottomGradient = "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)";
  const selectedGradient = top !== "unset" ? topGradient : bottomGradient;
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        top,
        bottom,
        zIndex: 10,
        width: "100%",
        height: "40px",
        position: "fixed",
        opacity: showGradient ? 1 : 0,
        transition: "0.25s all ease-in-out",
        visibility: showGradient ? "visible" : "hidden",
        background: selectedGradient
      }
    }
  );
}
function Dialog$1(props) {
  var _a, _b;
  const {
    id,
    open,
    title,
    children,
    action = "Got it",
    scrollBody = false,
    hideActions = false,
    hideCloseIcon = false,
    handleClose = () => null,
    handleAction = () => null,
    removePadding = false,
    dialogSx = {}
  } = props;
  const [showTop, setShowTop] = React__default.useState(false);
  const [showBottom, setShowBottom] = React__default.useState(false);
  const handleEventScroll = (entry, stateAction) => stateAction(!entry.isIntersecting);
  const handleTitleScroll = ([entry]) => handleEventScroll(entry, setShowTop);
  const handleActionsScroll = ([entry]) => handleEventScroll(entry, setShowBottom);
  const titleObserver = new IntersectionObserver(handleTitleScroll, { threshold: 1 });
  const actionsObserver = new IntersectionObserver(handleActionsScroll, { threshold: 1 });
  const titleRef = useRefWithCallback(
    (node) => titleObserver.observe(node),
    (node) => titleObserver.unobserve(node)
  );
  const actionRef = useRefWithCallback(
    (node) => actionsObserver.observe(node),
    (node) => actionsObserver.unobserve(node)
  );
  const theme = useTheme();
  const matches = useMediaQuery(`(max-width:${(_b = (_a = theme.breakpoints) == null ? void 0 : _a.values) == null ? void 0 : _b.md}px)`);
  return /* @__PURE__ */ jsxs(
    DialogControl,
    {
      matches,
      handleClose,
      id,
      open,
      scrollBody,
      sx: dialogSx,
      children: [
        matches && /* @__PURE__ */ jsxs(Box, { sx: { position: "relative" }, children: [
          /* @__PURE__ */ jsx(GradientComponent, { top: 0, showGradient: showTop }),
          /* @__PURE__ */ jsx(GradientComponent, { bottom: 0, showGradient: showBottom })
        ] }),
        /* @__PURE__ */ jsx(
          WrappedDialogTitle,
          {
            sx: {
              px: 0,
              maxWidth: "75%",
              mx: { ...responsiveDialogSpacing },
              py: removePadding ? { sx: 3, md: 3, lg: 0 } : 3
            },
            ref: titleRef,
            matches,
            id: `${id}-title`,
            scrollBody,
            hideCloseIcon,
            handleClose,
            children: /* @__PURE__ */ jsx(
              Typography$1,
              {
                sx: {
                  mt: {
                    xs: 6
                  }
                },
                component: "span",
                variant: scrollBody ? "h2" : "h4",
                children: title
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          DialogContent,
          {
            sx: {
              fontSize: {
                xs: "14px",
                md: "16px"
              },
              py: removePadding ? "0px" : void 0,
              px: removePadding ? "0px" : { ...responsiveDialogSpacing }
            },
            children
          }
        ),
        !hideActions && /* @__PURE__ */ jsx(
          DialogActions,
          {
            sx: {
              px: { ...responsiveDialogSpacing },
              py: 4,
              float: "left"
            },
            children: /* @__PURE__ */ jsx(
              Button$1,
              {
                id: "dialog-confirm",
                ref: actionRef,
                color: "primary",
                onClick: () => {
                  handleAction();
                  handleClose();
                },
                size: "large",
                variant: "contained",
                disableElevation: true,
                children: /* @__PURE__ */ jsx(Box, { component: "span", sx: { mx: 2 }, children: action })
              }
            )
          }
        )
      ]
    }
  );
}
const useCalendlyDialogDimensions = () => {
  const bp1 = useMediaQuery("(min-width:600px)");
  const bp2 = useMediaQuery("(min-width:725px)");
  const bp3 = useMediaQuery("(min-width:1127px)");
  const bp4 = useMediaQuery("(min-width:1666px)");
  let height = "900px";
  if (bp1)
    height = "985px";
  if (bp2)
    height = "896px";
  if (bp3)
    height = "985px";
  if (bp4)
    height = "700px";
  return { height };
};
const getCalendlyUtmFromPathname = (pathname) => {
  switch (pathname) {
    case "/results":
      return "purchase_playback";
    case "/playback":
      return "refinace_playback";
    default:
      return pathname.slice(1);
  }
};
const CalendlyLoadingScreen = () => {
  const { height } = useCalendlyDialogDimensions();
  return /* @__PURE__ */ jsx(LoadingScreen, { height });
};
const CalendlyDialog = ({
  isCalendlyOpen,
  handleClose,
  bookingUrl = "https://calendly.com/loan-experts/athena-consultation-1",
  hideEventTypeDetails = false,
  hideLandingPageDetails = false,
  hideGdprBanner = false,
  backgroundColor = "ffffff",
  textColor = "000000",
  primaryColor = "0975e0",
  prefill,
  utmContent
}) => {
  const { height } = useCalendlyDialogDimensions();
  const { pathname } = useLocation();
  return /* @__PURE__ */ jsx(
    Dialog$1,
    {
      id: "calendly-dialog",
      open: isCalendlyOpen,
      title: "",
      action: "Close",
      handleClose,
      removePadding: true,
      dialogSx: {
        maxWidth: "1000px"
      },
      children: /* @__PURE__ */ jsx(
        InlineWidget,
        {
          url: bookingUrl,
          pageSettings: {
            backgroundColor,
            hideEventTypeDetails,
            hideLandingPageDetails,
            primaryColor,
            hideGdprBanner,
            textColor
          },
          styles: {
            height,
            overflowY: "hidden"
          },
          prefill,
          utm: {
            utmMedium: "apply",
            utmContent,
            utmSource: getCalendlyUtmFromPathname(pathname)
          },
          LoadingSpinner: CalendlyLoadingScreen
        }
      )
    }
  );
};
const changeFavicon = (url) => {
  Array.prototype.slice.call(document.getElementsByTagName("link")).forEach((e) => {
    if (e.rel === "icon" && e.sizes.value === "32x32") {
      e.href = `${url}favicon.ico`;
    } else if (e.rel === "icon" && e.type === "image/svg+xml") {
      e.href = `${url}icon.svg`;
    } else if (e.rel === "apple-touch-icon") {
      e.href = `${url}apple-touch-icon.png`;
    }
  });
};
const FaviconChanger = ({ url, children }) => {
  useEffect(() => {
    changeFavicon(url);
  }, [url]);
  return children;
};
const ICON_DEFAULTS = {
  "aria-hidden": "true",
  style: {
    flexShrink: 0
  }
};
function ArrowForward({ size = 24, ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" })
      ]
    }
  );
}
function Close({ size = 24, ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0V0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" })
      ]
    }
  );
}
function Home({ size = 24, ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" })
      ]
    }
  );
}
function Logout({ size = 24, ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" })
      ]
    }
  );
}
function Person({ size = 24, ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      fill: "currentColor",
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" })
      ]
    }
  );
}
const Phone = ({ size = 24, ...props }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 0 24 24",
      width: size,
      ...props,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M0 0h24v24H0z", fill: "none" }),
        /* @__PURE__ */ jsx("path", { d: "M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" })
      ]
    }
  );
};
function Refresh({ size = 24, ...props }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      ...ICON_DEFAULTS,
      height: size,
      viewBox: "0 -960 960 960",
      width: size,
      ...props,
      children: /* @__PURE__ */ jsx("path", { d: "M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z" })
    }
  );
}
function Link({ style, ...props }) {
  const anchorRef = React__default.useRef(null);
  const [originalTextDecoration, setOriginalTextDecoration] = React__default.useState(void 0);
  return /* @__PURE__ */ jsx(
    "a",
    {
      ref: anchorRef,
      onMouseEnter: (e) => {
        setOriginalTextDecoration(e.currentTarget.style.textDecoration);
        e.currentTarget.style.textDecoration = "underline";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.textDecoration = originalTextDecoration || "none";
      },
      style: {
        textDecoration: "none",
        color: cssVar.primaryMain,
        cursor: "pointer",
        ...style
      },
      ...props,
      children: props.children
    }
  );
}
const MaxWidthContainer = ({ maxWidth, ...props }) => /* @__PURE__ */ jsx(Container, { maxWidth, ...props, children: props.children });
const DialogButton = ({ children, onClick, style, ...props }) => {
  const baseColor = cssVar.dialogButtonBase;
  const activeColor = cssVar.dialogButtonActive;
  const styleFocused = (e) => {
    e.currentTarget.style.backgroundColor = activeColor;
  };
  const styleBlurred = (e) => {
    e.currentTarget.style.backgroundColor = baseColor;
  };
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      style: {
        border: "none",
        borderRadius: "100%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "0.3s",
        backgroundColor: baseColor,
        height: 40,
        width: 40,
        zIndex: 1e3,
        ...style
      },
      onMouseEnter: styleFocused,
      onMouseLeave: styleBlurred,
      onFocus: styleFocused,
      onBlur: styleBlurred,
      onClick,
      ...props,
      children
    }
  );
};
var reactIs = { exports: {} };
var reactIs_production = {};
/**
 * @license React
 * react-is.production.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_production;
function requireReactIs_production() {
  if (hasRequiredReactIs_production)
    return reactIs_production;
  hasRequiredReactIs_production = 1;
  var REACT_ELEMENT_TYPE = Symbol.for("react.transitional.element"), REACT_PORTAL_TYPE = Symbol.for("react.portal"), REACT_FRAGMENT_TYPE = Symbol.for("react.fragment"), REACT_STRICT_MODE_TYPE = Symbol.for("react.strict_mode"), REACT_PROFILER_TYPE = Symbol.for("react.profiler");
  var REACT_CONSUMER_TYPE = Symbol.for("react.consumer"), REACT_CONTEXT_TYPE = Symbol.for("react.context"), REACT_FORWARD_REF_TYPE = Symbol.for("react.forward_ref"), REACT_SUSPENSE_TYPE = Symbol.for("react.suspense"), REACT_SUSPENSE_LIST_TYPE = Symbol.for("react.suspense_list"), REACT_MEMO_TYPE = Symbol.for("react.memo"), REACT_LAZY_TYPE = Symbol.for("react.lazy"), REACT_OFFSCREEN_TYPE = Symbol.for("react.offscreen"), REACT_CLIENT_REFERENCE = Symbol.for("react.client.reference");
  function typeOf(object) {
    if ("object" === typeof object && null !== object) {
      var $$typeof = object.$$typeof;
      switch ($$typeof) {
        case REACT_ELEMENT_TYPE:
          switch (object = object.type, object) {
            case REACT_FRAGMENT_TYPE:
            case REACT_PROFILER_TYPE:
            case REACT_STRICT_MODE_TYPE:
            case REACT_SUSPENSE_TYPE:
            case REACT_SUSPENSE_LIST_TYPE:
              return object;
            default:
              switch (object = object && object.$$typeof, object) {
                case REACT_CONTEXT_TYPE:
                case REACT_FORWARD_REF_TYPE:
                case REACT_LAZY_TYPE:
                case REACT_MEMO_TYPE:
                  return object;
                case REACT_CONSUMER_TYPE:
                  return object;
                default:
                  return $$typeof;
              }
          }
        case REACT_PORTAL_TYPE:
          return $$typeof;
      }
    }
  }
  reactIs_production.ContextConsumer = REACT_CONSUMER_TYPE;
  reactIs_production.ContextProvider = REACT_CONTEXT_TYPE;
  reactIs_production.Element = REACT_ELEMENT_TYPE;
  reactIs_production.ForwardRef = REACT_FORWARD_REF_TYPE;
  reactIs_production.Fragment = REACT_FRAGMENT_TYPE;
  reactIs_production.Lazy = REACT_LAZY_TYPE;
  reactIs_production.Memo = REACT_MEMO_TYPE;
  reactIs_production.Portal = REACT_PORTAL_TYPE;
  reactIs_production.Profiler = REACT_PROFILER_TYPE;
  reactIs_production.StrictMode = REACT_STRICT_MODE_TYPE;
  reactIs_production.Suspense = REACT_SUSPENSE_TYPE;
  reactIs_production.SuspenseList = REACT_SUSPENSE_LIST_TYPE;
  reactIs_production.isContextConsumer = function(object) {
    return typeOf(object) === REACT_CONSUMER_TYPE;
  };
  reactIs_production.isContextProvider = function(object) {
    return typeOf(object) === REACT_CONTEXT_TYPE;
  };
  reactIs_production.isElement = function(object) {
    return "object" === typeof object && null !== object && object.$$typeof === REACT_ELEMENT_TYPE;
  };
  reactIs_production.isForwardRef = function(object) {
    return typeOf(object) === REACT_FORWARD_REF_TYPE;
  };
  reactIs_production.isFragment = function(object) {
    return typeOf(object) === REACT_FRAGMENT_TYPE;
  };
  reactIs_production.isLazy = function(object) {
    return typeOf(object) === REACT_LAZY_TYPE;
  };
  reactIs_production.isMemo = function(object) {
    return typeOf(object) === REACT_MEMO_TYPE;
  };
  reactIs_production.isPortal = function(object) {
    return typeOf(object) === REACT_PORTAL_TYPE;
  };
  reactIs_production.isProfiler = function(object) {
    return typeOf(object) === REACT_PROFILER_TYPE;
  };
  reactIs_production.isStrictMode = function(object) {
    return typeOf(object) === REACT_STRICT_MODE_TYPE;
  };
  reactIs_production.isSuspense = function(object) {
    return typeOf(object) === REACT_SUSPENSE_TYPE;
  };
  reactIs_production.isSuspenseList = function(object) {
    return typeOf(object) === REACT_SUSPENSE_LIST_TYPE;
  };
  reactIs_production.isValidElementType = function(type) {
    return "string" === typeof type || "function" === typeof type || type === REACT_FRAGMENT_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || type === REACT_OFFSCREEN_TYPE || "object" === typeof type && null !== type && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_CONSUMER_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_CLIENT_REFERENCE || void 0 !== type.getModuleId) ? true : false;
  };
  reactIs_production.typeOf = typeOf;
  return reactIs_production;
}
var reactIs_development = {};
/**
 * @license React
 * react-is.development.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_development;
function requireReactIs_development() {
  if (hasRequiredReactIs_development)
    return reactIs_development;
  hasRequiredReactIs_development = 1;
  "production" !== process.env.NODE_ENV && function() {
    function typeOf(object) {
      if ("object" === typeof object && null !== object) {
        var $$typeof = object.$$typeof;
        switch ($$typeof) {
          case REACT_ELEMENT_TYPE:
            switch (object = object.type, object) {
              case REACT_FRAGMENT_TYPE:
              case REACT_PROFILER_TYPE:
              case REACT_STRICT_MODE_TYPE:
              case REACT_SUSPENSE_TYPE:
              case REACT_SUSPENSE_LIST_TYPE:
                return object;
              default:
                switch (object = object && object.$$typeof, object) {
                  case REACT_CONTEXT_TYPE:
                  case REACT_FORWARD_REF_TYPE:
                  case REACT_LAZY_TYPE:
                  case REACT_MEMO_TYPE:
                    return object;
                  case REACT_CONSUMER_TYPE:
                    return object;
                  default:
                    return $$typeof;
                }
            }
          case REACT_PORTAL_TYPE:
            return $$typeof;
        }
      }
    }
    var REACT_ELEMENT_TYPE = Symbol.for("react.transitional.element"), REACT_PORTAL_TYPE = Symbol.for("react.portal"), REACT_FRAGMENT_TYPE = Symbol.for("react.fragment"), REACT_STRICT_MODE_TYPE = Symbol.for("react.strict_mode"), REACT_PROFILER_TYPE = Symbol.for("react.profiler");
    var REACT_CONSUMER_TYPE = Symbol.for("react.consumer"), REACT_CONTEXT_TYPE = Symbol.for("react.context"), REACT_FORWARD_REF_TYPE = Symbol.for("react.forward_ref"), REACT_SUSPENSE_TYPE = Symbol.for("react.suspense"), REACT_SUSPENSE_LIST_TYPE = Symbol.for("react.suspense_list"), REACT_MEMO_TYPE = Symbol.for("react.memo"), REACT_LAZY_TYPE = Symbol.for("react.lazy"), REACT_OFFSCREEN_TYPE = Symbol.for("react.offscreen"), REACT_CLIENT_REFERENCE = Symbol.for("react.client.reference");
    reactIs_development.ContextConsumer = REACT_CONSUMER_TYPE;
    reactIs_development.ContextProvider = REACT_CONTEXT_TYPE;
    reactIs_development.Element = REACT_ELEMENT_TYPE;
    reactIs_development.ForwardRef = REACT_FORWARD_REF_TYPE;
    reactIs_development.Fragment = REACT_FRAGMENT_TYPE;
    reactIs_development.Lazy = REACT_LAZY_TYPE;
    reactIs_development.Memo = REACT_MEMO_TYPE;
    reactIs_development.Portal = REACT_PORTAL_TYPE;
    reactIs_development.Profiler = REACT_PROFILER_TYPE;
    reactIs_development.StrictMode = REACT_STRICT_MODE_TYPE;
    reactIs_development.Suspense = REACT_SUSPENSE_TYPE;
    reactIs_development.SuspenseList = REACT_SUSPENSE_LIST_TYPE;
    reactIs_development.isContextConsumer = function(object) {
      return typeOf(object) === REACT_CONSUMER_TYPE;
    };
    reactIs_development.isContextProvider = function(object) {
      return typeOf(object) === REACT_CONTEXT_TYPE;
    };
    reactIs_development.isElement = function(object) {
      return "object" === typeof object && null !== object && object.$$typeof === REACT_ELEMENT_TYPE;
    };
    reactIs_development.isForwardRef = function(object) {
      return typeOf(object) === REACT_FORWARD_REF_TYPE;
    };
    reactIs_development.isFragment = function(object) {
      return typeOf(object) === REACT_FRAGMENT_TYPE;
    };
    reactIs_development.isLazy = function(object) {
      return typeOf(object) === REACT_LAZY_TYPE;
    };
    reactIs_development.isMemo = function(object) {
      return typeOf(object) === REACT_MEMO_TYPE;
    };
    reactIs_development.isPortal = function(object) {
      return typeOf(object) === REACT_PORTAL_TYPE;
    };
    reactIs_development.isProfiler = function(object) {
      return typeOf(object) === REACT_PROFILER_TYPE;
    };
    reactIs_development.isStrictMode = function(object) {
      return typeOf(object) === REACT_STRICT_MODE_TYPE;
    };
    reactIs_development.isSuspense = function(object) {
      return typeOf(object) === REACT_SUSPENSE_TYPE;
    };
    reactIs_development.isSuspenseList = function(object) {
      return typeOf(object) === REACT_SUSPENSE_LIST_TYPE;
    };
    reactIs_development.isValidElementType = function(type) {
      return "string" === typeof type || "function" === typeof type || type === REACT_FRAGMENT_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || type === REACT_OFFSCREEN_TYPE || "object" === typeof type && null !== type && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_CONSUMER_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_CLIENT_REFERENCE || void 0 !== type.getModuleId) ? true : false;
    };
    reactIs_development.typeOf = typeOf;
  }();
  return reactIs_development;
}
if (process.env.NODE_ENV === "production") {
  reactIs.exports = requireReactIs_production();
} else {
  reactIs.exports = requireReactIs_development();
}
var reactIsExports = reactIs.exports;
function isPlainObject(item) {
  if (typeof item !== "object" || item === null) {
    return false;
  }
  const prototype = Object.getPrototypeOf(item);
  return (prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null) && !(Symbol.toStringTag in item) && !(Symbol.iterator in item);
}
function deepClone(source) {
  if (/* @__PURE__ */ React.isValidElement(source) || reactIsExports.isValidElementType(source) || !isPlainObject(source)) {
    return source;
  }
  const output = {};
  Object.keys(source).forEach((key) => {
    output[key] = deepClone(source[key]);
  });
  return output;
}
function deepmerge(target, source, options = {
  clone: true
}) {
  const output = options.clone ? {
    ...target
  } : target;
  if (isPlainObject(target) && isPlainObject(source)) {
    Object.keys(source).forEach((key) => {
      if (/* @__PURE__ */ React.isValidElement(source[key]) || reactIsExports.isValidElementType(source[key])) {
        output[key] = source[key];
      } else if (isPlainObject(source[key]) && // Avoid prototype pollution
      Object.prototype.hasOwnProperty.call(target, key) && isPlainObject(target[key])) {
        output[key] = deepmerge(target[key], source[key], options);
      } else if (options.clone) {
        output[key] = isPlainObject(source[key]) ? deepClone(source[key]) : source[key];
      } else {
        output[key] = source[key];
      }
    });
  }
  return output;
}
const getSharedBreakpoints = () => ({
  values: {
    xs: 300,
    sm: 600,
    md: 756,
    lg: 1200,
    xl: 1536
  }
});
const getSharedDesignTokens = () => ({
  typography: {
    fontFamily: ["Inter", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "64px",
      letterSpacing: "-2px",
      lineHeight: "72px",
      "@media (max-width: 724px)": {
        fontSize: "48px",
        letterSpacing: "-1.5px",
        lineHeight: "56px"
      }
    },
    h2: {
      fontWeight: 700,
      fontSize: "48px",
      letterSpacing: "-1.5px",
      lineHeight: "56px",
      "@media (max-width: 724px)": {
        fontSize: "40px",
        letterSpacing: "-1.25px",
        lineHeight: "48px"
      }
    },
    h3: {
      fontWeight: 700,
      fontSize: "40px",
      letterSpacing: "-1.25px",
      lineHeight: "48px",
      "@media (max-width: 724px)": {
        fontSize: "32px",
        letterSpacing: "-1px",
        lineHeight: "40px"
      }
    },
    h4: {
      fontWeight: 700,
      fontSize: "32px",
      letterSpacing: "-1px",
      lineHeight: "40px",
      "@media (max-width: 724px)": {
        fontSize: "24px",
        letterSpacing: "-0.5px",
        lineHeight: "32px"
      }
    },
    h5: {
      fontWeight: 700,
      fontSize: "24px",
      letterSpacing: "-0.5px",
      lineHeight: "32px"
    },
    h6: {
      fontWeight: 700,
      fontSize: "20px",
      letterSpacing: "-0.25px",
      lineHeight: "24px"
    },
    overline: {
      // Used within MUI Selects as titles needs review before adjusting size
      fontWeight: 700
    },
    body1: {
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "20px"
    },
    body2: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "18px"
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "18px",
      letterSpacing: "-0.25px",
      lineHeight: "24px",
      "@media (max-width: 724px)": {
        fontSize: "16px",
        fontWeight: 500,
        lineHeight: "20px"
      },
      body2: {
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "18px"
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: "18px",
        letterSpacing: "-0.25px",
        lineHeight: "24px",
        "@media (max-width: 724px)": {
          fontSize: "16px",
          lineHeight: "20px"
        }
      },
      subtitle2: {
        fontWeight: 500
      },
      caption: {
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "14px"
      }
    },
    subtitle2: {
      fontWeight: "bold"
    },
    caption: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "14px"
    }
  },
  shape: {},
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)"
    }
  },
  spacing: [0, 4, 8, 16, 24, 32, 48, 64, 128, 256, 384, 512, 768, 1152],
  breakpoints: getSharedBreakpoints()
});
const getSafeColor = (theme, ownerState) => {
  let color;
  if (ownerState.color === "inherit" || ownerState.color === "default" || ownerState.color === void 0) {
    color = theme.palette.primary.main;
  } else if (ownerState.color in theme.palette && "main" in theme.palette[ownerState.color]) {
    color = theme.palette[ownerState.color].main;
  } else {
    color = theme.palette.primary.main;
  }
  return color;
};
let AthenaTheme = createTheme({
  ...deepmerge(getAthenaLightPalette(), getSharedDesignTokens())
});
AthenaTheme = createTheme(AthenaTheme, {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none"
        },
        outlined: ({ ownerState }) => ({
          border: `2px solid ${getSafeColor(AthenaTheme, ownerState)}`,
          "&:hover": {
            border: `2px solid ${getSafeColor(AthenaTheme, ownerState)}`
          },
          "&:disabled": {
            borderWidth: "2px"
          }
        }),
        text: ({ ownerState }) => ({
          backgroundColor: alpha(getSafeColor(AthenaTheme, ownerState), 0.05),
          transition: AthenaTheme.transitions.create(["background-color"], {
            duration: AthenaTheme.transitions.duration.standard
          }),
          "&:hover": {
            backgroundColor: alpha(getSafeColor(AthenaTheme, ownerState), 0.1)
          }
        }),
        sizeSmall: {
          fontSize: "12px",
          lineHeight: "16px",
          padding: "8px 12px"
        },
        sizeMedium: {
          fontSize: "14px",
          lineHeight: "20px",
          padding: "10px 14px"
        },
        sizeLarge: {
          fontSize: "16px",
          lineHeight: "24px",
          padding: "12px 16px"
        },
        outlinedSizeSmall: {
          padding: "6px 10px"
        },
        outlinedSizeMedium: {
          padding: "8px 12px"
        },
        outlinedSizeLarge: {
          padding: "10px 14px"
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: ownerState.color === "default" ? alpha(AthenaTheme.palette.common.black, 0.05) : alpha(getSafeColor(AthenaTheme, ownerState), 0.05),
          transition: AthenaTheme.transitions.create(["background-color"], {
            duration: AthenaTheme.transitions.duration.standard
          }),
          ".MuiInputBase-root &, .MuiPickersCalendarHeader-root &": {
            backgroundColor: "transparent"
          },
          "&:hover": {
            backgroundColor: ownerState.color === "default" ? alpha(AthenaTheme.palette.common.black, 0.1) : alpha(getSafeColor(AthenaTheme, ownerState), 0.1)
          }
        })
      }
    },
    MuiCard: {
      defaultProps: {},
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: "2px",
          background: AthenaTheme.palette.common.white,
          transition: AthenaTheme.transitions.create(["box-shadow"], {
            duration: AthenaTheme.transitions.duration.standard
          })
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          color: AthenaTheme.palette.text.secondary
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "transparent"
        },
        barColorPrimary: {
          backgroundColor: AthenaTheme.palette.info.main
        }
      }
    }
  }
});
let DraftViewerTheme = createTheme({
  ...deepmerge(getDraftViewerLightPalette(), getSharedDesignTokens())
});
DraftViewerTheme = createTheme(DraftViewerTheme, {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none"
        },
        outlined: ({ ownerState }) => ({
          border: `2px solid ${getSafeColor(DraftViewerTheme, ownerState)}`,
          "&:hover": {
            border: `2px solid ${getSafeColor(DraftViewerTheme, ownerState)}`
          },
          "&:disabled": {
            borderWidth: "2px"
          }
        }),
        text: ({ ownerState }) => ({
          backgroundColor: alpha(getSafeColor(DraftViewerTheme, ownerState), 0.05),
          transition: DraftViewerTheme.transitions.create(["background-color"], {
            duration: DraftViewerTheme.transitions.duration.standard
          }),
          "&:hover": {
            backgroundColor: alpha(getSafeColor(DraftViewerTheme, ownerState), 0.1)
          }
        }),
        sizeSmall: {
          fontSize: "12px",
          lineHeight: "16px",
          padding: "8px 12px"
        },
        sizeMedium: {
          fontSize: "14px",
          lineHeight: "20px",
          padding: "10px 14px"
        },
        sizeLarge: {
          fontSize: "16px",
          lineHeight: "24px",
          padding: "12px 16px"
        },
        outlinedSizeSmall: {
          padding: "6px 10px"
        },
        outlinedSizeMedium: {
          padding: "8px 12px"
        },
        outlinedSizeLarge: {
          padding: "10px 14px"
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: ownerState.color === "default" ? alpha(DraftViewerTheme.palette.common.black, 0.05) : alpha(getSafeColor(DraftViewerTheme, ownerState), 0.05),
          transition: DraftViewerTheme.transitions.create(["background-color"], {
            duration: DraftViewerTheme.transitions.duration.standard
          }),
          ".MuiInputBase-root &, .MuiPickersCalendarHeader-root &": {
            backgroundColor: "transparent"
          },
          "&:hover": {
            backgroundColor: ownerState.color === "default" ? alpha(DraftViewerTheme.palette.common.black, 0.1) : alpha(getSafeColor(DraftViewerTheme, ownerState), 0.1)
          }
        })
      }
    },
    MuiCard: {
      defaultProps: {},
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: "2px",
          background: "#FFFFFF",
          transition: DraftViewerTheme.transitions.create(["box-shadow"], {
            duration: DraftViewerTheme.transitions.duration.standard
          })
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          color: DraftViewerTheme.palette.text.secondary
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "transparent"
        },
        barColorPrimary: {
          backgroundColor: DraftViewerTheme.palette.info.main
        }
      }
    }
  }
});
let LmgTheme = createTheme({
  ...deepmerge(getLmgLightDesignTokens(), getSharedDesignTokens())
});
LmgTheme = createTheme(LmgTheme, {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "24px",
          textTransform: "none"
        },
        outlined: ({ ownerState }) => ({
          border: `2px solid ${getSafeColor(LmgTheme, ownerState)}`,
          "&:hover": {
            border: `2px solid ${getSafeColor(LmgTheme, ownerState)}`
          },
          "&:disabled": {
            borderWidth: "2px"
          }
        }),
        text: ({ ownerState }) => ({
          backgroundColor: alpha(getSafeColor(LmgTheme, ownerState), 0.05),
          transition: LmgTheme.transitions.create(["background-color"], {
            duration: LmgTheme.transitions.duration.standard
          }),
          "&:hover": {
            backgroundColor: alpha(getSafeColor(LmgTheme, ownerState), 0.1)
          }
        }),
        sizeSmall: {
          fontSize: "12px",
          lineHeight: "16px",
          padding: "8px 12px"
        },
        sizeMedium: {
          fontSize: "14px",
          lineHeight: "20px",
          padding: "10px 14px"
        },
        sizeLarge: {
          fontSize: "16px",
          lineHeight: "24px",
          padding: "12px 32px"
        },
        outlinedSizeSmall: {
          padding: "6px 10px"
        },
        outlinedSizeMedium: {
          padding: "8px 12px"
        },
        outlinedSizeLarge: {
          padding: "10px 14px"
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: ownerState.color === "default" ? alpha(LmgTheme.palette.common.black, 0.05) : alpha(getSafeColor(LmgTheme, ownerState), 0.05),
          transition: LmgTheme.transitions.create(["background-color"], {
            duration: LmgTheme.transitions.duration.standard
          }),
          ".MuiInputBase-root &, .MuiPickersCalendarHeader-root &": {
            backgroundColor: "transparent"
          },
          "&:hover": {
            backgroundColor: ownerState.color === "default" ? alpha(LmgTheme.palette.common.black, 0.1) : alpha(getSafeColor(LmgTheme, ownerState), 0.1)
          }
        })
      }
    },
    MuiCard: {
      defaultProps: {},
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: "2px",
          background: "#FFFFFF",
          transition: LmgTheme.transitions.create(["box-shadow"], {
            duration: LmgTheme.transitions.duration.standard
          })
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          color: LmgTheme.palette.text.secondary
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "transparent"
        },
        barColorPrimary: {
          backgroundColor: LmgTheme.palette.info.main
        }
      }
    }
  }
});
let MortgageChoiceTheme = createTheme({
  ...deepmerge(getMortgageChoiceLightDesignTokens(), getSharedDesignTokens())
});
MortgageChoiceTheme = createTheme(MortgageChoiceTheme, {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "24px",
          textTransform: "none"
        },
        outlined: ({ ownerState }) => ({
          border: `2px solid ${getSafeColor(MortgageChoiceTheme, ownerState)}`,
          "&:hover": {
            border: `2px solid ${getSafeColor(MortgageChoiceTheme, ownerState)}`
          },
          "&:disabled": {
            borderWidth: "2px"
          }
        }),
        text: ({ ownerState }) => ({
          backgroundColor: alpha(getSafeColor(MortgageChoiceTheme, ownerState), 0.05),
          transition: MortgageChoiceTheme.transitions.create(["background-color"], {
            duration: MortgageChoiceTheme.transitions.duration.standard
          }),
          "&:hover": {
            backgroundColor: alpha(getSafeColor(MortgageChoiceTheme, ownerState), 0.1)
          }
        }),
        sizeSmall: {
          fontSize: "12px",
          lineHeight: "16px",
          padding: "8px 12px"
        },
        sizeMedium: {
          fontSize: "14px",
          lineHeight: "20px",
          padding: "10px 14px"
        },
        sizeLarge: {
          fontSize: "16px",
          lineHeight: "24px",
          padding: "12px 32px"
        },
        outlinedSizeSmall: {
          padding: "6px 10px"
        },
        outlinedSizeMedium: {
          padding: "8px 12px"
        },
        outlinedSizeLarge: {
          padding: "10px 14px"
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: ownerState.color === "default" ? alpha(MortgageChoiceTheme.palette.common.black, 0.05) : alpha(getSafeColor(MortgageChoiceTheme, ownerState), 0.05),
          transition: MortgageChoiceTheme.transitions.create(["background-color"], {
            duration: MortgageChoiceTheme.transitions.duration.standard
          }),
          ".MuiInputBase-root &, .MuiPickersCalendarHeader-root &": {
            backgroundColor: "transparent"
          },
          "&:hover": {
            backgroundColor: ownerState.color === "default" ? alpha(MortgageChoiceTheme.palette.common.black, 0.1) : alpha(getSafeColor(MortgageChoiceTheme, ownerState), 0.1)
          }
        })
      }
    },
    MuiCard: {
      defaultProps: {},
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: "2px",
          background: "#FFFFFF",
          transition: MortgageChoiceTheme.transitions.create(["box-shadow"], {
            duration: MortgageChoiceTheme.transitions.duration.standard
          })
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          color: MortgageChoiceTheme.palette.text.secondary
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "transparent"
        },
        barColorPrimary: {
          backgroundColor: MortgageChoiceTheme.palette.info.main
        }
      }
    }
  }
});
const themes = {
  athenaLight: AthenaTheme,
  mortgageChoiceLight: MortgageChoiceTheme,
  lmgLight: LmgTheme,
  draftViewerLight: DraftViewerTheme
};
const useWindowSize = () => {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const resize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return {
    isMobile: size ? size.width <= getSharedBreakpoints().values.sm : false
  };
};
const Dialog = ({ children, id, open: isOpen, label, closeDialog }) => {
  const dialogRef = useRef(null);
  const sectionRef = useRef(null);
  const previouslyFocusedElement = useRef(null);
  const { isMobile } = useWindowSize();
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      const focusableElements = dialogRef.current ? Array.from(
        dialogRef.current.querySelectorAll(
          'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"])'
        )
      ) : [];
      if (focusableElements.length === 0)
        return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };
  const handleClickOutside = (e) => {
    if (isOpen && sectionRef.current && !sectionRef.current.contains(e.target)) {
      closeDialog();
    }
  };
  const sizedDialogStyles = isMobile ? {
    margin: "5rem 0px 0px 0px",
    minHeight: "calc(100vh - 5rem)",
    maxWidth: "unset",
    borderRadius: "24px 24px 0px 0px",
    padding: "0px 0px 5rem"
  } : {
    margin: "auto",
    minHeight: "unset",
    borderRadius: "24px",
    maxWidth: "calc(100% - 3rem)",
    padding: "0px"
  };
  useEffect(() => {
    if (dialogRef.current) {
      if (isOpen) {
        previouslyFocusedElement.current = document.activeElement;
        document.addEventListener("click", handleClickOutside, { capture: true });
        if (!dialogRef.current.open) {
          dialogRef.current.showModal();
        }
        dialogRef.current.addEventListener("close", closeDialog);
        dialogRef.current.addEventListener("keydown", handleKeyDown);
        dialogRef.current.style.transform = "translateY(0)";
      } else if (dialogRef.current) {
        dialogRef.current.style.transform = "translateY(100vh)";
        setTimeout(() => {
          var _a;
          if ((_a = dialogRef.current) == null ? void 0 : _a.open) {
            dialogRef.current.close();
          }
          if (previouslyFocusedElement.current) {
            previouslyFocusedElement.current.focus();
          }
        }, 200);
      }
    }
    return () => {
      if (dialogRef.current) {
        dialogRef.current.removeEventListener("close", closeDialog);
        dialogRef.current.removeEventListener("keydown", handleKeyDown);
      }
      document.removeEventListener("click", handleClickOutside, { capture: true });
    };
  }, [isOpen]);
  return /* @__PURE__ */ jsxs(
    "dialog",
    {
      "aria-label": label,
      "aria-modal": "true",
      id,
      ref: dialogRef,
      style: {
        border: "none",
        boxShadow: "rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px",
        transform: "translateY(100vh)",
        transition: "200ms ease-in",
        outline: "200vh solid rgba(0,0,0,0.45)",
        ...sizedDialogStyles
      },
      children: [
        /* @__PURE__ */ jsx("section", { ref: sectionRef, style: { margin: 0, padding: 0, width: "100%", maxWidth: 900 }, children }),
        /* @__PURE__ */ jsx(
          DialogButton,
          {
            "aria-label": "Close Modal",
            onClick: (e) => {
              e.preventDefault();
              closeDialog();
            },
            style: {
              position: "absolute",
              right: "10px",
              top: "10px"
            },
            title: "Close Modal",
            children: /* @__PURE__ */ jsx(Close, {})
          }
        )
      ]
    }
  );
};
function PoweredByAthenaLogo({ ...props }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: "166",
      height: "24",
      viewBox: "0 0 166 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: { display: "block" },
      ...props,
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M5.89388 8.95255C5.89388 10.3816 4.89368 11.2365 3.29582 11.2365H1.62478V13.777H0.722168V6.65637H3.28363C4.90589 6.65637 5.89388 7.51133 5.89388 8.95255ZM3.19824 10.4182C4.29601 10.4182 4.93027 9.91744 4.93027 8.95255C4.93027 7.98767 4.29601 7.49912 3.19824 7.49912H1.62478V10.4182H3.19824Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.33301 11.2121C6.33301 9.63658 7.44297 8.56177 8.91885 8.56177C10.4069 8.56177 11.5047 9.63658 11.5047 11.2121C11.5047 12.7999 10.4069 13.887 8.91885 13.887C7.44297 13.887 6.33301 12.7999 6.33301 11.2121ZM10.6143 11.2121C10.6143 10.0518 9.89465 9.35566 8.91885 9.35566C7.95526 9.35566 7.2356 10.0518 7.2356 11.2121C7.2356 12.3969 7.95526 13.0931 8.91885 13.0931C9.89465 13.0931 10.6143 12.3847 10.6143 11.2121Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M18.2498 8.64709H19.1524L17.5179 13.7891H16.7007L15.4688 9.94175L14.2368 13.7891H13.4318L11.7852 8.64709H12.7L13.8709 12.5188L15.115 8.64709H15.8469L17.1032 12.5188L18.2498 8.64709Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M24.2267 11.4076H20.3235C20.3845 12.5312 21.0432 13.1053 21.897 13.1053C22.58 13.1053 23.1411 12.8121 23.4704 11.9816L24.2145 12.287C23.751 13.4595 22.8484 13.887 21.8848 13.887C20.4577 13.887 19.4209 12.8121 19.4209 11.2121C19.4209 9.59994 20.4577 8.56177 21.8848 8.56177C23.2387 8.56177 24.2267 9.59994 24.2267 11.1633C24.2389 11.2488 24.2389 11.3343 24.2267 11.4076ZM20.3601 10.7358H23.3241C23.1899 9.78314 22.6166 9.30681 21.8848 9.30681C21.0676 9.30681 20.5065 9.81978 20.3601 10.7358Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M27.7396 8.54944V9.47768H27.2517C26.5199 9.47768 26.0076 10.0884 26.0076 11.0288V13.7891H25.1294V8.64715H26.0076V9.53875C26.3003 8.89142 26.8248 8.56165 27.4347 8.56165H27.7396V8.54944Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M32.6793 11.4076H28.7761C28.8371 12.5312 29.4958 13.1053 30.3496 13.1053C31.0327 13.1053 31.5937 12.8121 31.9231 11.9816L32.6671 12.287C32.2036 13.4595 31.301 13.887 30.3374 13.887C28.9103 13.887 27.8735 12.8121 27.8735 11.2121C27.8735 9.59994 28.9103 8.56177 30.3374 8.56177C31.6913 8.56177 32.6793 9.59994 32.6793 11.1633C32.6915 11.2488 32.6915 11.3343 32.6793 11.4076ZM28.8005 10.7358H31.7645C31.6303 9.78314 31.0571 9.30681 30.3252 9.30681C29.5202 9.30681 28.9469 9.81978 28.8005 10.7358Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M37.4972 6.65625H38.3754V13.7769H37.4972V12.812C37.1557 13.4349 36.558 13.8746 35.7042 13.8746C34.3625 13.8746 33.3257 12.7998 33.3257 11.212C33.3257 9.6364 34.3625 8.56159 35.7042 8.56159C36.558 8.56159 37.1435 9.00129 37.4972 9.62419V6.65625ZM37.5216 11.212C37.5216 10.0395 36.8386 9.35549 35.875 9.35549C34.9114 9.35549 34.2161 10.0517 34.2161 11.212C34.2161 12.3967 34.9114 13.0807 35.875 13.0807C36.8386 13.0807 37.5216 12.3845 37.5216 11.212Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M46.6088 11.2121C46.6088 12.7999 45.5842 13.8747 44.2303 13.8747C43.3765 13.8747 42.791 13.4228 42.4373 12.7999V13.7892H41.5591V6.66864H42.4373V9.63658C42.7788 9.00146 43.3765 8.56177 44.2303 8.56177C45.5842 8.54955 46.6088 9.63658 46.6088 11.2121ZM45.7306 11.2121C45.7306 10.0396 45.0476 9.35566 44.084 9.35566C43.1204 9.35566 42.4251 10.0518 42.4251 11.2121C42.4251 12.3969 43.1204 13.0808 44.084 13.0808C45.0476 13.0808 45.7306 12.3847 45.7306 11.2121Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M50.8293 8.64709H51.7807L48.4752 15.4624H47.5238L48.8167 12.7998L46.731 8.64709H47.7312L49.2924 11.8471L50.8293 8.64709Z",
            fill: "#18161B"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: "M67.2346 16.1588L66.8564 0L63.7583 17.6122L67.4175 24L67.2346 16.1588Z", fill: "#B40000" }),
        /* @__PURE__ */ jsx("path", { d: "M77.5536 24L66.8564 0L67.4175 24H77.5536Z", fill: "#FF0000" }),
        /* @__PURE__ */ jsx("path", { d: "M66.8564 0L50.0361 24H62.6361L66.8564 0Z", fill: "#FF00A0" }),
        /* @__PURE__ */ jsx("path", { d: "M59.5991 10.3573L67.4177 24H77.5537L59.5991 10.3573Z", fill: "#B40000" }),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M93.1541 9.52651L94.7032 13.1418H91.5319L93.1541 9.52651ZM88.019 17.2334H89.7145L90.8732 14.6563H95.3375L96.4352 17.2334H98.1551L93.1785 5.93567L88.019 17.2334Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: "M105.973 8.10984V17.2335H104.4V8.10984H101.997V6.59534H108.364V8.10984H105.973Z", fill: "black" }),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M115.549 10.7602H120.049V6.59534H121.623V17.2335H120.049V12.2625H115.549V17.2335H113.975V6.59534H115.549V10.7602Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M134.013 8.28617H129.829V10.8388H133.891V12.3411H129.829V15.8953H134.013V17.4098H128.255V6.77167H134.013V8.28617Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M140.358 17.2334H141.932V9.78303L149.543 17.8807V6.59524H147.97V13.9845L140.358 5.87463V17.2334Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M161.814 13.142L160.264 9.52676L158.642 13.142H161.814ZM162.46 14.6443H157.996L156.837 17.2214H155.142L160.301 5.92371L165.278 17.2214H163.558L162.46 14.6443Z",
            fill: "black"
          }
        )
      ]
    }
  );
}
const FOOTER_POST_LOGO = {
  athena: null,
  draftViewer: null,
  mortgageChoice: /* @__PURE__ */ jsx(PoweredByAthenaLogo, { style: { minWidth: 140 } }),
  lmg: null
};
const MUILessFooter = ({
  activeBrand,
  backgroundColor
}) => {
  return /* @__PURE__ */ jsxs(
    "footer",
    {
      style: {
        backgroundColor: backgroundColor || cssVar.backgroundDefault,
        display: "flex",
        flexDirection: "column",
        // 72px = 16px Normal Padding + 60px Make space for Intercom widget
        padding: "32px 72px",
        margin: 0,
        boxSizing: "border-box",
        gap: "16px",
        alignItems: "center"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "span",
          {
            style: {
              fontWeight: 400,
              fontSize: "11px",
              letterSpacing: "1.5px",
              lineHeight: "12px",
              color: cssVar.footerText,
              textAlign: "center",
              textWrap: "balance"
            },
            children: [
              "Athena Mortgage PTY LTD | ABN 24 619 536 506 | Australian Credit Licence 502611 |",
              " ",
              /* @__PURE__ */ jsx(Link, { style: { color: "inherit" }, target: "_blank", href: "/privacy-policy", children: "Contact & Legal" })
            ]
          }
        ),
        FOOTER_POST_LOGO[activeBrand]
      ]
    }
  );
};
const contactDetails = {
  athena: {
    mail: {
      href: "mailto:hello@athena.com.au",
      label: "hello@athena.com.au"
    },
    phone: {
      href: "tel:13-35-35",
      label: "13 35 35"
    },
    text: {
      href: "sms:+61429333555",
      label: "0429 333 555"
    },
    calendlyBookingUrl: "https://calendly.com/loan-experts/athena-consultation-1"
  },
  draftViewer: {
    mail: {
      href: "mailto:hello@athena.com.au",
      label: "hello@athena.com.au"
    },
    phone: {
      href: "tel:13-35-35",
      label: "13 35 35"
    },
    text: {
      href: "sms:+61429333555",
      label: "0429 333 555"
    },
    calendlyBookingUrl: "https://calendly.com/loan-experts/athena-consultation-1"
  },
  mortgageChoice: {
    mail: {
      href: "mailto:hello@mortgagechoice.athena.com.au",
      label: "hello@mortgagechoice.athena.com.au"
    },
    phone: {
      href: "tel:13-34-34",
      label: "13 34 34"
    },
    text: {
      href: "sms:+61483900880",
      label: "0483 900 880"
    },
    calendlyBookingUrl: "https://calendly.com/mortgage-choice-freedom/home-loan-consultation"
  },
  lmg: {
    mail: {
      href: "mailto:hello@lmg.athena.com.au",
      label: "hello@lmg.athena.com.au"
    },
    phone: {
      href: "tel:13-15-25",
      label: "13 15 25"
    },
    text: {
      href: "sms:+61483988185",
      label: "0483 988 185"
    },
    calendlyBookingUrl: ""
  }
};
function useBrandContact({ activeBrand }) {
  const activeBrandContactDetails = contactDetails[activeBrand];
  if (!activeBrandContactDetails) {
    throw new Error(`No contact details found for brand: ${activeBrand}`);
  }
  return activeBrandContactDetails;
}
const IconButton = ({ children, ...props }) => {
  const styleFocused = (e) => {
    e.currentTarget.style.backgroundColor = cssVar.primaryMain0_1;
  };
  const styleBlurred = (e) => {
    e.currentTarget.style.backgroundColor = "transparent";
  };
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      style: {
        appearance: "none",
        alignItems: "center",
        border: "none",
        borderRadius: "50%",
        background: "none",
        cursor: "pointer",
        display: "inline-flex",
        justifyContent: "center",
        padding: 5,
        transition: "0.3s"
      },
      onMouseEnter: styleFocused,
      onMouseLeave: styleBlurred,
      onFocus: styleFocused,
      onBlur: styleBlurred,
      ...props,
      children
    }
  );
};
const TRANSITION_DURATION = 200;
function Popover({ anchorEl, open, onClose, children }) {
  const popoverRef = React__default.useRef(null);
  const [popoverOpen, setPopoverOpen] = React__default.useState(open);
  const openPopver = () => {
    setPopoverOpen(true);
    setTimeout(() => {
      if (popoverRef.current && anchorEl) {
        popoverRef.current.style.right = `${window.innerWidth - anchorEl.offsetLeft - anchorEl.clientWidth}px`;
        popoverRef.current.style.top = `${anchorEl.offsetTop + anchorEl.offsetHeight}px`;
        popoverRef.current.style.opacity = "1";
      }
    }, TRANSITION_DURATION);
  };
  const closePopover = () => {
    onClose();
    if (popoverRef.current)
      popoverRef.current.style.opacity = "0";
    setTimeout(() => {
      setPopoverOpen(false);
    }, TRANSITION_DURATION);
  };
  const handleClickOutside = (event) => {
    if (popoverRef.current && !popoverRef.current.contains(event.target) && // check if the popover is clicked
    anchorEl && !anchorEl.contains(event.target)) {
      closePopover();
    }
  };
  const handleEscapeDown = (event) => {
    if (event.key === "Escape") {
      closePopover();
    }
  };
  const handleTabDown = (event) => {
    if (!popoverRef.current)
      return;
    const modalElement = popoverRef.current;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.key === "Tab") {
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };
  useEffect(() => {
    if (open) {
      openPopver();
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeDown);
      document.addEventListener("keydown", handleTabDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeDown);
      document.removeEventListener("keydown", handleTabDown);
    };
  }, [open]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: popoverRef,
      role: "dialog",
      tabIndex: -1,
      style: {
        backgroundColor: "white",
        display: popoverOpen ? "block" : "none",
        opacity: 0,
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        position: "absolute",
        zIndex: 1,
        transition: `opacity ${TRANSITION_DURATION}ms`
      },
      children
    }
  );
}
const TYPOPGRAHY_STYLES = getSharedDesignTokens().typography;
function Typography({
  component,
  children,
  elRef,
  variant = "body1",
  style,
  ...rest
}) {
  const Element = component || "p";
  return React__default.createElement(
    Element,
    {
      ref: elRef,
      style: {
        ...TYPOPGRAHY_STYLES[variant],
        color: cssVar.textPrimary,
        margin: 0,
        ...style
      },
      ...rest
    },
    children
  );
}
function Divider() {
  return /* @__PURE__ */ jsx(
    "hr",
    {
      style: {
        borderWidth: 0,
        borderStyle: "solid",
        borderColor: cssVar.dividerColor,
        borderBottomWidth: "thin",
        width: "100%",
        margin: 0,
        padding: 0
      }
    }
  );
}
const MUILessContactUs = ({ activeBrand, openCalendlyDialog }) => {
  const [phoneAnchorEl, setPhoneAnchorEl] = useState(null);
  const phoneOpen = !!phoneAnchorEl;
  const { mail, phone, text } = useBrandContact({ activeBrand });
  const handlePhoneClick = (event) => {
    setPhoneAnchorEl(event.currentTarget);
  };
  const handlePhoneClose = () => {
    setPhoneAnchorEl(null);
  };
  const contactDetails2 = [
    {
      heading: "SMS",
      component: /* @__PURE__ */ jsx(Link, { href: text.href, tabIndex: 0, children: text.label })
    },
    {
      heading: "LOCAL CALL",
      component: /* @__PURE__ */ jsx(Link, { href: phone.href, tabIndex: 0, children: phone.label })
    },
    {
      heading: "EMAIL",
      component: /* @__PURE__ */ jsx(Link, { href: mail.href, tabIndex: 0, children: activeBrand === BRAND_KEYS.MORTGAGE_CHOICE ? "Email us" : mail.label })
    },
    {
      heading: "Weekdays 9AM - 5PM AET",
      component: null
    }
  ];
  if (openCalendlyDialog) {
    contactDetails2.unshift({
      heading: "Arrange a call",
      component: (
        /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
        /* @__PURE__ */ jsx(
          Link,
          {
            role: "button",
            onClick: openCalendlyDialog,
            onKeyDown: (event) => {
              if (event.key === "Enter") {
                openCalendlyDialog();
              }
            },
            tabIndex: 0,
            children: "Book appointment"
          }
        )
      )
    });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      IconButton,
      {
        "aria-expanded": phoneOpen,
        "aria-label": "Phone",
        "aria-haspopup": "true",
        onClick: handlePhoneClick,
        title: "Contact us",
        "data-testid": "PhoneIcon",
        children: /* @__PURE__ */ jsx(Phone, { fill: cssVar.primaryMain })
      }
    ),
    /* @__PURE__ */ jsxs(Popover, { anchorEl: phoneAnchorEl, open: phoneOpen, onClose: handlePhoneClose, children: [
      activeBrand === BRAND_KEYS.MORTGAGE_CHOICE && /* @__PURE__ */ jsx(
        Typography,
        {
          component: "h3",
          variant: "body1",
          style: {
            padding: "16px",
            paddingBottom: 0,
            width: "216px",
            fontWeight: 700,
            color: cssVar.textPrimary
          },
          children: "Contact the Mortgage Choice Freedom team"
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            display: "flex",
            flexDirection: "column",
            padding: "0px 16px"
          },
          children: contactDetails2.map((item, index) => /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "16px 0"
                },
                children: [
                  /* @__PURE__ */ jsx(Typography, { variant: "body1", style: { fontWeight: 700 }, children: item.heading }),
                  item.component && /* @__PURE__ */ jsx(Typography, { variant: "body1", children: item.component })
                ]
              }
            ),
            index !== contactDetails2.length - 1 && /* @__PURE__ */ jsx(Divider, {})
          ] }, item.heading))
        }
      )
    ] })
  ] });
};
function AthenaLogo() {
  return /* @__PURE__ */ jsx("span", { style: { display: "block", height: "clamp(24px, 100%, 32px)", width: "clamp(106px, 100%, 144px)" }, children: /* @__PURE__ */ jsxs("svg", { fill: "none", height: "100%", width: "100%", viewBox: "0 0 134 24", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsx("path", { d: "M17.166 16.17 16.794.045l-3.096 17.582L17.345 24l-.18-7.83Z", fill: "#B40000" }),
    /* @__PURE__ */ jsx("path", { d: "M27.464 24 16.794.045 17.345 24h10.119Z", fill: "#FF0001" }),
    /* @__PURE__ */ jsx("path", { d: "M16.794.045 0 24h12.576L16.794.045Z", fill: "#FF009F" }),
    /* @__PURE__ */ jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.55 10.38 17.344 24h10.119L9.549 10.38Z", fill: "#B40000" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M37.92 17.243h1.692l1.16-2.577h4.457l1.093 2.577h1.72l-4.97-11.277-5.153 11.277Zm5.124-7.689 1.546 3.607h-3.164l1.618-3.607ZM55.834 8.138v9.105h-1.57V8.138h-2.395V6.633h6.353v1.505h-2.388ZM65.394 10.784h4.491v-4.15h1.572v10.609h-1.572v-4.955h-4.491v4.955h-1.57V6.633h1.57v4.15ZM83.896 8.138h-4.174v2.55h4.053v1.504h-4.053v3.546h4.174v1.505h-5.745V6.633h5.745v1.505ZM90.159 17.243h1.57V9.814l7.595 8.075V6.633h-1.572v7.373L90.16 5.911v11.332ZM112.214 14.666h-4.458l-1.159 2.577h-1.693l5.152-11.277 4.97 11.277h-1.719l-1.093-2.577Zm-.641-1.505-1.544-3.607-1.618 3.607h3.162Z",
        fill: "#1A1919"
      }
    )
  ] }) });
}
function MortgagechoiceLogo() {
  return /* @__PURE__ */ jsx("span", { style: { display: "block", height: "clamp(32px, 100%, 40px)", width: "clamp(144px, 100%, 180px)" }, children: /* @__PURE__ */ jsxs("svg", { width: "100%", height: "100%", viewBox: "0 0 256 48", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M80.8503 0.838867H84.0327V18.9593H80.6524V6.47764L75.9104 13.2873H75.3404L70.5588 6.47764V18.951H67.2021V0.838867H70.3846L75.6649 8.44581L80.8503 0.838867Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M86.1938 12.2824C86.1938 8.16339 88.9805 5.32324 92.7329 5.32324C96.5329 5.32324 99.3195 8.17169 99.3195 12.2824C99.3195 16.4264 96.5329 19.2748 92.7329 19.2748C88.9805 19.2665 86.1938 16.4181 86.1938 12.2824ZM96.0579 12.2824C96.0579 9.74955 94.625 8.29626 92.725 8.29626C90.8488 8.29626 89.4396 9.74955 89.4396 12.2824C89.4396 14.8485 90.8488 16.3184 92.725 16.3184C94.6329 16.3184 96.0579 14.8402 96.0579 12.2824Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M108.305 5.31494V8.88589H106.975C105.494 8.88589 104.434 9.99869 104.434 11.9669V18.9593H101.251V5.63051H104.434V7.54886C105.146 5.99591 106.31 5.32325 107.64 5.32325H108.305V5.31494Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M117.298 8.29615H114.456V14.1758C114.456 15.4464 114.947 16.0941 115.937 16.0941H117.29V18.9675H115.541C112.825 18.9675 111.274 17.182 111.274 14.3335V8.29615H108.954V5.6304H111.274V3.0643L114.456 1.229V5.6304H117.298V8.29615Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M127.415 5.63051H130.598V17.2236C130.598 21.3427 127.954 23.7178 124.083 23.7178C122.08 23.7178 119.515 22.7295 118.28 20.0389L120.647 18.3863C121.391 19.8063 122.65 20.7946 124.154 20.7946C125.927 20.7946 127.407 19.6818 127.407 17.4063V15.8285C126.671 17.016 125.508 17.8963 123.703 17.8963C120.568 17.8963 118.248 15.1807 118.248 11.6098C118.248 8.03882 120.568 5.32324 123.703 5.32324C125.508 5.32324 126.663 6.20352 127.407 7.41598V5.63051H127.415ZM127.463 11.6098C127.463 9.61668 126.228 8.27135 124.502 8.27135C122.729 8.27135 121.494 9.61668 121.494 11.6098C121.494 13.6029 122.729 14.9482 124.502 14.9482C126.236 14.9482 127.463 13.6029 127.463 11.6098Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M144.056 10.5717V18.9593H141.095V17.6638C140.327 18.7268 139.021 19.2665 137.343 19.2665C134.382 19.2665 132.435 17.7136 132.435 15.0977C132.435 12.5897 134.461 10.9537 137.564 10.9537C139.021 10.9537 140.129 11.3939 140.874 12.1164V10.8956C140.874 9.11011 139.884 8.27966 138.229 8.27966C136.82 8.27966 135.862 9.06029 135.395 10.4056L132.751 9.24299C133.614 6.41945 135.839 5.30664 138.356 5.30664C141.42 5.31495 144.056 6.97585 144.056 10.5717ZM140.945 15.0728C140.945 13.935 139.884 13.2873 138.332 13.2873C136.781 13.2873 135.72 13.935 135.72 15.0728C135.72 16.2105 136.781 16.8333 138.332 16.8333C139.884 16.8333 140.945 16.2105 140.945 15.0728Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M155.021 5.63051H158.203V17.2236C158.203 21.3427 155.559 23.7178 151.688 23.7178C149.693 23.7178 147.12 22.7295 145.893 20.0389L148.26 18.3863C149.004 19.8063 150.263 20.7946 151.767 20.7946C153.54 20.7946 155.021 19.6818 155.021 17.4063V15.8285C154.284 17.016 153.121 17.8963 151.316 17.8963C148.181 17.8963 145.861 15.1807 145.861 11.6098C145.861 8.03882 148.181 5.32324 151.316 5.32324C153.121 5.32324 154.276 6.20352 155.021 7.41598V5.63051ZM155.068 11.6098C155.068 9.61668 153.833 8.27135 152.107 8.27135C150.334 8.27135 149.099 9.61668 149.099 11.6098C149.099 13.6029 150.334 14.9482 152.107 14.9482C153.833 14.9482 155.068 13.6029 155.068 11.6098Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M172.302 13.0298H163.341C163.539 15.3883 164.726 16.4181 166.349 16.4181C167.853 16.4181 168.938 15.6956 169.603 13.8852L172.295 14.9233C171.305 17.9295 169.065 19.2748 166.468 19.2748C162.715 19.2748 160.103 16.4762 160.103 12.2824C160.103 8.13848 162.668 5.32324 166.325 5.32324C169.856 5.32324 172.318 8.12187 172.318 12.2575C172.326 12.49 172.326 12.7973 172.302 13.0298ZM163.444 10.7544H169.096C168.748 8.86097 167.742 8.01391 166.333 8.01391C164.774 8.0056 163.784 8.9191 163.444 10.7544Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M178.58 9.899C178.58 4.33497 182.182 0.531494 187.241 0.531494C190.969 0.531494 193.851 2.59932 194.967 6.30314L191.88 7.49069C191.112 5.19033 189.457 3.76195 187.241 3.76195C184.256 3.76195 182.111 6.06231 182.111 9.899C182.111 13.7357 184.256 16.036 187.241 16.036C189.457 16.036 191.112 14.616 191.88 12.3073L194.967 13.4949C193.859 17.1987 190.969 19.2665 187.241 19.2665C182.182 19.2665 178.58 15.463 178.58 9.899Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M207.705 10.3642V18.9593H204.523V10.9372C204.523 9.20152 203.589 8.22159 202.306 8.22159C200.802 8.22159 199.717 9.28457 199.717 11.1199V18.9593H196.535V0.838867H199.717V7.36622C200.485 5.91293 201.768 5.32331 203.193 5.32331C205.781 5.31501 207.705 7.30809 207.705 10.3642Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M209.518 12.2824C209.518 8.16339 212.305 5.32324 216.057 5.32324C219.857 5.32324 222.644 8.17169 222.644 12.2824C222.644 16.4264 219.857 19.2748 216.057 19.2748C212.305 19.2665 209.518 16.4181 209.518 12.2824ZM219.382 12.2824C219.382 9.74955 217.949 8.29626 216.049 8.29626C214.173 8.29626 212.764 9.74955 212.764 12.2824C212.764 14.8485 214.173 16.3184 216.049 16.3184C217.957 16.3184 219.382 14.8402 219.382 12.2824Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M224.132 2.10931C224.132 0.863629 225.042 0.0913086 226.182 0.0913086C227.315 0.0913086 228.233 0.871934 228.233 2.10931C228.233 3.3799 227.322 4.10239 226.182 4.10239C225.042 4.10239 224.132 3.3716 224.132 2.10931ZM227.758 5.63043V18.9592H224.575V5.63043H227.758Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M229.689 12.2824C229.689 8.11356 232.452 5.32324 236.054 5.32324C238.865 5.32324 241.287 6.80145 242.126 10.0568L239.316 11.1198C238.698 9.07689 237.661 8.27135 236.062 8.27135C234.455 8.27135 232.975 9.59177 232.975 12.2824C232.975 14.9731 234.455 16.2935 236.062 16.2935C237.669 16.2935 238.706 15.5129 239.316 13.47L242.126 14.533C241.287 17.7966 238.873 19.2665 236.054 19.2665C232.452 19.2665 229.689 16.443 229.689 12.2824Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M255.386 13.0298H246.425C246.623 15.3883 247.81 16.4181 249.433 16.4181C250.937 16.4181 252.022 15.6956 252.687 13.8852L255.378 14.9233C254.389 17.9295 252.149 19.2748 249.552 19.2748C245.799 19.2748 243.187 16.4762 243.187 12.2824C243.187 8.13848 245.752 5.32324 249.409 5.32324C252.94 5.32324 255.402 8.12187 255.402 12.2575C255.41 12.49 255.41 12.7973 255.386 13.0298ZM246.52 10.7544H252.172C251.824 8.86097 250.819 8.01391 249.409 8.01391C247.858 8.0056 246.868 8.9191 246.52 10.7544Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M27.5085 16.9411C27.5085 16.9411 38.5838 0.772197 50.2686 0.772197C54.1635 0.772197 56.7998 2.69885 57.061 5.38951C57.3381 8.15492 55.7944 11.6013 50.5298 11.2359C46.8407 10.9868 34.0001 8.73624 25.8539 24.3737C25.8539 24.3737 25.2998 14.2006 20.2411 8.22135C17.5653 5.05733 17.8424 2.12583 20.5182 0.381884C20.5182 0.381884 25.0148 -2.01812 26.4081 5.21512C27.0414 8.45388 27.9914 13.5529 27.5085 16.9411Z",
        fill: "#5AE2E0"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M24.4369 30.9093C24.4369 30.9093 31.7359 13.229 51.9152 16.0774C55.3193 16.5591 57.5201 18.984 57.2114 22.5384C56.8631 26.5411 52.501 27.4629 48.7486 26.3834C36.0504 22.7294 30.7543 24.3903 23.376 35.8422C20.9773 21.766 12.4987 19.6982 9.83082 15.4214C8.97583 14.0511 8.47709 11.5183 9.98915 9.99023C11.5012 8.4622 13.797 8.18815 15.4991 9.58331C18.9507 12.4401 23.851 24.274 24.4369 30.9093Z",
        fill: "#E4002B"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M21.1674 47.834C21.1674 47.834 21.7374 33.5502 4.13891 27.7122C1.38395 26.7904 -0.310191 23.3357 0.893125 20.944C2.04894 18.6353 5.24723 18.1952 8.21594 19.7564C18.6895 25.2789 22.2045 40.8582 22.2045 40.8582C31.0077 27.189 44.7271 28.9745 50.7595 30.6021C56.9423 32.263 57.8923 36.4817 56.8631 39.3551C55.8815 42.1205 52.4457 43.8811 46.3341 40.6091C45.7958 40.36 29.2186 30.9758 21.1674 47.834Z",
        fill: "#006875"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M141.309 48H69.4503C68.2074 48 67.21 46.9454 67.21 45.6499V29.7052C67.21 28.4014 68.2154 27.355 69.4503 27.355H141.309C142.552 27.355 143.549 28.4097 143.549 29.7052V45.6415C143.549 46.9454 142.544 48 141.309 48Z",
        fill: "#FF00A0"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M82.0379 33.4173H76.1717V36.9218H81.3254V38.7073H76.1717V43.5405H74.2876V31.6318H82.0379V33.4173Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M87.3816 34.6545V36.6642H86.5346C85.4816 36.6642 84.7375 37.5279 84.7375 38.9231V43.5321H82.9404V34.8455H84.7375V36.1909C85.2046 35.1528 85.9883 34.6628 86.9225 34.6628H87.3816V34.6545Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M95.5515 39.604H89.519C89.6457 41.2899 90.5245 42.0871 91.7199 42.0871C92.7569 42.0871 93.5486 41.6137 94.0236 40.3348L95.5435 40.9494C94.8469 42.9009 93.3902 43.7231 91.7515 43.7231C89.3845 43.7231 87.6982 41.9044 87.6982 39.1805C87.6982 36.4566 89.3686 34.6545 91.704 34.6545C93.9602 34.6545 95.5594 36.4566 95.5594 39.1307C95.5673 39.2802 95.5673 39.4546 95.5515 39.604ZM89.5745 38.2587H93.7227C93.4932 36.8635 92.7332 36.2158 91.6961 36.2158C90.564 36.2241 89.812 36.9217 89.5745 38.2587Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M104.386 39.604H98.354C98.4807 41.2899 99.3594 42.0871 100.555 42.0871C101.592 42.0871 102.384 41.6137 102.859 40.3348L104.379 40.9494C103.682 42.9009 102.225 43.7231 100.586 43.7231C98.2194 43.7231 96.5332 41.9044 96.5332 39.1805C96.5332 36.4566 98.2036 34.6545 100.539 34.6545C102.795 34.6545 104.394 36.4566 104.394 39.1307C104.402 39.2802 104.402 39.4546 104.386 39.604ZM98.4094 38.2587H102.558C102.328 36.8635 101.568 36.2158 100.531 36.2158C99.399 36.2241 98.6469 36.9217 98.4094 38.2587Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M111.844 31.6318H113.625V43.5322H111.844V42.1038C111.305 43.0423 110.435 43.7232 109.136 43.7232C106.983 43.7232 105.376 41.8215 105.376 39.1806C105.376 36.5647 106.983 34.6547 109.136 34.6547C110.435 34.6547 111.305 35.3357 111.844 36.2741V31.6318ZM111.875 39.1806C111.875 37.3952 110.902 36.3571 109.54 36.3571C108.163 36.3571 107.189 37.3952 107.189 39.1806C107.189 40.9827 108.163 42.0374 109.54 42.0374C110.902 42.0374 111.875 40.9827 111.875 39.1806Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M114.955 39.1805C114.955 36.5148 116.752 34.6545 119.182 34.6545C121.613 34.6545 123.41 36.5065 123.41 39.1805C123.41 41.8712 121.613 43.7231 119.182 43.7231C116.752 43.7231 114.955 41.8629 114.955 39.1805ZM121.589 39.1805C121.589 37.395 120.568 36.357 119.19 36.357C117.813 36.357 116.807 37.395 116.807 39.1805C116.807 40.9826 117.813 42.0373 119.19 42.0373C120.568 42.0373 121.589 40.9826 121.589 39.1805Z",
        fill: "white"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M136.488 37.8686V43.5323H134.691V38.2257C134.691 37.0216 134.073 36.324 133.219 36.324C132.229 36.324 131.516 37.0382 131.516 38.3005V43.5406H129.719V38.234C129.719 37.0299 129.102 36.3323 128.247 36.3323C127.257 36.3323 126.545 37.0465 126.545 38.3088V43.5489H124.748V34.8458H126.545V36.0001C127.028 35.0617 127.827 34.6714 128.73 34.6714C129.799 34.6714 130.693 35.2361 131.16 36.1828C131.659 35.1115 132.585 34.6714 133.606 34.6714C135.269 34.6714 136.488 35.9337 136.488 37.8686Z",
        fill: "white"
      }
    )
  ] }) });
}
function LmgLogo() {
  return /* @__PURE__ */ jsx("span", { style: { display: "block", height: "clamp(24px, 100%, 32px)", width: "clamp(92px, 100%, 122px)" }, children: /* @__PURE__ */ jsxs("svg", { width: "100%", height: "100%", viewBox: "0 0 300 79", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M279.187 49.2573C275.439 49.2573 271.997 48.3776 268.861 46.6182C265.724 44.8588 263.238 42.4301 261.402 39.3321C259.567 36.1958 258.649 32.6579 258.649 28.7184C258.649 24.8171 259.567 21.3175 261.402 18.2194C263.238 15.0831 265.724 12.6353 268.861 10.8759C271.997 9.11651 275.439 8.23682 279.187 8.23682C282.974 8.23682 286.416 9.11651 289.514 10.8759C292.651 12.6353 295.118 15.0831 296.915 18.2194C298.751 21.3175 299.669 24.8171 299.669 28.7184C299.669 32.6579 298.751 36.1958 296.915 39.3321C295.118 42.4301 292.651 44.8588 289.514 46.6182C286.378 48.3776 282.936 49.2573 279.187 49.2573ZM279.187 42.0859C281.597 42.0859 283.72 41.5504 285.556 40.4795C287.392 39.3703 288.826 37.8022 289.859 35.775C290.891 33.7479 291.408 31.3957 291.408 28.7184C291.408 26.041 290.891 23.7079 289.859 21.7191C288.826 19.692 287.392 18.1429 285.556 17.072C283.72 16.0011 281.597 15.4656 279.187 15.4656C276.778 15.4656 274.636 16.0011 272.762 17.072C270.926 18.1429 269.492 19.692 268.459 21.7191C267.426 23.7079 266.91 26.041 266.91 28.7184C266.91 31.3957 267.426 33.7479 268.459 35.775C269.492 37.8022 270.926 39.3703 272.762 40.4795C274.636 41.5504 276.778 42.0859 279.187 42.0859Z",
        fill: "#20344E"
      }
    ),
    /* @__PURE__ */ jsx("path", { d: "M244.594 42.4875H257.789V48.8557H236.562V8.81055H244.594V42.4875Z", fill: "#20344E" }),
    /* @__PURE__ */ jsx("path", { d: "M220.556 42.4875H233.751V48.8557H212.524V8.81055H220.556V42.4875Z", fill: "#20344E" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M188.362 49.2573C184.613 49.2573 181.171 48.3776 178.035 46.6182C174.898 44.8588 172.412 42.4301 170.576 39.3321C168.741 36.1958 167.823 32.6579 167.823 28.7184C167.823 24.8171 168.741 21.3175 170.576 18.2194C172.412 15.0831 174.898 12.6353 178.035 10.8759C181.171 9.11651 184.613 8.23682 188.362 8.23682C192.148 8.23682 195.59 9.11651 198.688 10.8759C201.825 12.6353 204.292 15.0831 206.089 18.2194C207.925 21.3175 208.843 24.8171 208.843 28.7184C208.843 32.6579 207.925 36.1958 206.089 39.3321C204.292 42.4301 201.825 44.8588 198.688 46.6182C195.552 48.3776 192.11 49.2573 188.362 49.2573ZM188.362 42.0859C190.771 42.0859 192.894 41.5504 194.73 40.4795C196.566 39.3703 198 37.8022 199.033 35.775C200.065 33.7479 200.582 31.3957 200.582 28.7184C200.582 26.041 200.065 23.7079 199.033 21.7191C198 19.692 196.566 18.1429 194.73 17.072C192.894 16.0011 190.771 15.4656 188.362 15.4656C185.952 15.4656 183.81 16.0011 181.936 17.072C180.1 18.1429 178.666 19.692 177.633 21.7191C176.6 23.7079 176.084 26.041 176.084 28.7184C176.084 31.3957 176.6 33.7479 177.633 35.775C178.666 37.8022 180.1 39.3703 181.936 40.4795C183.81 41.5504 185.952 42.0859 188.362 42.0859Z",
        fill: "#20344E"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M166.365 21.2027C166.365 23.3446 165.849 25.3526 164.816 27.2267C163.822 29.1009 162.235 30.6116 160.055 31.7591C157.913 32.9065 155.197 33.4802 151.908 33.4802H145.195V48.8557H137.163V8.81055H151.908C155.006 8.81055 157.645 9.34601 159.825 10.4169C162.005 11.4879 163.631 12.9604 164.702 14.8345C165.811 16.7087 166.365 18.8314 166.365 21.2027ZM151.564 26.9972C153.782 26.9972 155.427 26.5 156.498 25.5056C157.569 24.4729 158.104 23.0386 158.104 21.2027C158.104 17.3015 155.924 15.3509 151.564 15.3509H145.195V26.9972H151.564Z",
        fill: "#20344E"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M122.946 41.2253H106.996L104.357 48.8557H95.9238L110.324 8.75317H119.676L134.076 48.8557H125.585L122.946 41.2253ZM120.766 34.7997L114.971 18.0473L109.177 34.7997H120.766Z",
        fill: "#20344E"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M151.621 72.4238C151.621 71.8208 151.135 71.3308 150.532 71.3308C150.289 71.3308 150.055 71.3978 149.875 71.5318C149.745 71.6114 149.577 71.7077 149.46 71.7622C149.121 71.9297 149.058 71.9632 148.807 72.0678C148.807 71.6993 148.815 67.1557 148.815 67.1557C148.815 67.1557 146.604 67.1557 146.592 67.1473C146.579 67.4446 146.462 67.7964 146.462 67.7964C146.014 69.0652 144.778 70.0243 143.359 70.0243C141.554 70.0243 140.088 68.4957 140.088 66.6908C140.088 64.8859 141.571 63.437 143.375 63.437C144.284 63.437 145.13 63.86 145.679 64.4169L147.245 62.8549C146.257 61.8625 144.887 61.2468 143.375 61.2468C140.365 61.2468 137.923 63.6883 137.923 66.6992C137.923 69.7101 140.365 72.1516 143.375 72.1516C144.611 72.1516 145.708 71.7621 146.625 71.067C146.625 71.5611 146.625 72.4447 146.625 72.8886C146.462 72.9347 146.11 73.0435 145.917 73.077C144.984 73.3283 143.757 73.521 143.304 74.9825C143.124 75.6064 143.187 76.4105 143.505 76.98C143.84 77.5788 144.238 77.9013 144.971 78.1567C145.197 78.2446 145.553 78.2907 145.767 78.2949C145.959 78.2949 146.139 78.2824 146.324 78.2447C147.584 77.9767 148.669 76.4398 148.799 74.8736C148.811 74.7103 148.824 74.392 148.824 74.392C148.824 74.392 149.314 74.1826 149.393 74.1533C149.816 73.9816 150.478 73.6927 150.951 73.4288C151.345 73.2655 151.621 72.8761 151.621 72.4196M146.449 75.217C146.449 75.217 146.416 75.3678 146.353 75.5855C146.29 75.8033 146.085 76.0587 145.884 76.1048C145.821 76.1173 145.712 76.1131 145.633 76.0922C145.507 76.0587 145.432 76.0001 145.373 75.8996C145.323 75.8116 145.339 75.7237 145.352 75.6818C145.423 75.4431 145.917 75.3175 146.449 75.2211",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M150.52 73.2988C151.001 73.2988 151.395 72.9052 151.395 72.4194C151.395 71.9337 151.005 71.54 150.52 71.54C150.034 71.54 149.644 71.9337 149.644 72.4194C149.644 72.9052 150.034 73.2988 150.52 73.2988Z",
        fill: "#327E73"
      }
    ),
    /* @__PURE__ */ jsx("path", { d: "M125.699 69.9907H119.145V61.272H116.955V72.1809H125.699V69.9907Z", fill: "black" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M137.437 61.2341H135.26L131.989 67.792L128.714 61.2634H126.537V72.1724H128.714V65.6102L130.875 70.045H133.061L135.26 65.6186V72.1472H137.437V61.2341Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M96.9027 65.2312C97.1772 64.7509 97.5792 64.3587 98.1086 64.0548C98.638 63.7509 99.2409 63.5989 99.9174 63.5989C100.643 63.5989 101.295 63.7705 101.873 64.1136C102.452 64.4567 102.908 64.942 103.241 65.5695C103.574 66.1871 103.741 66.9077 103.741 67.7312C103.741 68.545 103.574 69.2704 103.241 69.9077C102.908 70.5449 102.447 71.04 101.859 71.393C101.28 71.7459 100.633 71.9224 99.9174 71.9224C99.2213 71.9224 98.6086 71.7704 98.0792 71.4665C97.5596 71.1626 97.1674 70.7753 96.9027 70.3048V71.79H95.5645V60.9077H96.9027V65.2312ZM102.373 67.7312C102.373 67.1234 102.251 66.594 102.006 66.143C101.761 65.692 101.427 65.3489 101.006 65.1136C100.594 64.8783 100.138 64.7607 99.638 64.7607C99.1478 64.7607 98.6919 64.8832 98.2703 65.1283C97.8586 65.3636 97.5253 65.7116 97.2704 66.1724C97.0253 66.6234 96.9027 67.1479 96.9027 67.7459C96.9027 68.3538 97.0253 68.8881 97.2704 69.3489C97.5253 69.7999 97.8586 70.1479 98.2703 70.393C98.6919 70.6283 99.1478 70.7459 99.638 70.7459C100.138 70.7459 100.594 70.6283 101.006 70.393C101.427 70.1479 101.761 69.7999 102.006 69.3489C102.251 68.8881 102.373 68.3489 102.373 67.7312ZM111.855 63.7312L107.002 75.5841H105.62L107.208 71.7018L103.958 63.7312H105.444L107.973 70.2606L110.473 63.7312H111.855Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M39 78C60.5391 78 78 60.5391 78 39C78 38.9007 77.9996 38.8015 77.9989 38.7023L11.2546 66.4079C18.3251 73.5649 28.1446 78 39 78Z",
        fill: "#D2295C"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M8.4727 63.2735C3.16917 56.6124 0 48.1763 0 39C0 17.4609 17.4609 0 39 0C60.4172 0 77.8022 17.2638 77.9983 38.6346L63.3499 35.8273L65.0024 39.8082L8.4727 63.2735Z",
        fill: "#EFC900"
      }
    )
  ] }) });
}
const SkipToContentLink = /* @__PURE__ */ jsx(
  "a",
  {
    type: "button",
    href: "#main",
    style: {
      position: "absolute",
      left: "-10000px",
      top: "auto",
      width: "1px",
      height: "1px",
      overflow: "hidden"
    },
    onFocus: (e) => {
      e.currentTarget.style.left = "0";
      e.currentTarget.style.position = "relative";
      e.currentTarget.style.width = "auto";
      e.currentTarget.style.height = "auto";
    },
    onBlur: (e) => {
      e.currentTarget.style.left = "-10000px";
      e.currentTarget.style.position = "absolute";
      e.currentTarget.style.width = "1px";
      e.currentTarget.style.height = "1px";
    },
    children: "Skip to main content"
  }
);
const BRAND_LOGO = {
  athena: /* @__PURE__ */ jsx(AthenaLogo, {}),
  draftViewer: /* @__PURE__ */ jsx(AthenaLogo, {}),
  mortgageChoice: /* @__PURE__ */ jsx(MortgagechoiceLogo, {}),
  lmg: /* @__PURE__ */ jsx(LmgLogo, {})
};
const MUILessHeader = ({ activeBrand, children, href = "/" }) => {
  return /* @__PURE__ */ jsxs(
    "header",
    {
      id: "header-testId",
      style: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        width: "100%",
        backgroundColor: "transparent",
        minHeight: "64px",
        position: "static",
        padding: "16px"
      },
      children: [
        SkipToContentLink,
        /* @__PURE__ */ jsx("a", { href, title: "To Home Page", children: BRAND_LOGO[activeBrand] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              justifyContent: "flex-end",
              width: "100%"
            },
            children
          }
        )
      ]
    }
  );
};
const KEYS = {
  APPLICATION_TRACKER: "applicationTracker",
  LOGOUT: "logout"
};
const profileMenuOptionKeys = {
  athena: [KEYS.APPLICATION_TRACKER, KEYS.LOGOUT],
  draftViewer: [KEYS.APPLICATION_TRACKER, KEYS.LOGOUT],
  mortgageChoice: [KEYS.APPLICATION_TRACKER, KEYS.LOGOUT],
  lmg: [KEYS.LOGOUT]
};
function useBrandProfileMenuOptions({ activeBrand }) {
  const activeProfileMenuOptionKeys = profileMenuOptionKeys[activeBrand];
  if (!activeProfileMenuOptionKeys) {
    throw new Error(`No profile menu options found for brand: ${activeBrand}`);
  }
  const profileMenuOptions = [];
  activeProfileMenuOptionKeys.forEach((profileMenuOptionKey) => {
    if (profileMenuOptionKey === KEYS.APPLICATION_TRACKER) {
      profileMenuOptions.push({
        title: "Application tracker",
        href: "/account/home",
        icon: /* @__PURE__ */ jsx(Home, { fill: cssVar.buttonPrimary })
      });
    } else if (profileMenuOptionKey === KEYS.LOGOUT) {
      profileMenuOptions.push({
        title: "Log out",
        href: "/account/logout",
        icon: /* @__PURE__ */ jsx(Logout, { fill: cssVar.buttonPrimary })
      });
    }
  });
  return profileMenuOptions;
}
function MUILessProfileOptions({ activeBrand }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = menuAnchorEl !== null;
  const profileMenuOptions = useBrandProfileMenuOptions({ activeBrand });
  const handleProfileClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setMenuAnchorEl(null);
  };
  const styleFocused = (e) => {
    e.currentTarget.style.backgroundColor = cssVar.primaryMain0_1;
  };
  const styleBlurred = (e) => {
    e.currentTarget.style.backgroundColor = "transparent";
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      IconButton,
      {
        color: "primary",
        "aria-label": "Profile",
        "aria-haspopup": "true",
        onClick: handleProfileClick,
        title: "Profile options",
        "data-testid": "PersonIcon",
        children: /* @__PURE__ */ jsx(Person, { fill: cssVar.primaryMain })
      }
    ),
    /* @__PURE__ */ jsx(Popover, { anchorEl: menuAnchorEl, open: menuOpen, onClose: handleProfileClose, children: /* @__PURE__ */ jsx("ul", { style: { listStyle: "none", padding: 0, margin: 0 }, children: profileMenuOptions.map((option) => /* @__PURE__ */ jsx(
      "li",
      {
        style: {
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: 0,
          margin: 0
        },
        children: /* @__PURE__ */ jsxs(
          "a",
          {
            href: option.href,
            style: {
              alignItems: "center",
              boxSizing: "border-box",
              color: cssVar.textPrimary,
              display: "flex",
              fontSize: "16px",
              gap: "4px",
              justifyContent: "flex-start",
              lineHeight: "32px",
              padding: "8px 16px",
              textDecoration: "none",
              flexGrow: 1
            },
            onFocus: styleFocused,
            onBlur: styleBlurred,
            onMouseEnter: styleFocused,
            onMouseLeave: styleBlurred,
            children: [
              option.icon,
              option.title
            ]
          }
        )
      },
      `profile-button-${option.title}`
    )) }) })
  ] });
}
function RadioGroupOption({
  name,
  value,
  label,
  caption,
  checked
}) {
  const inputRef = React__default.useRef(null);
  const labelRef = React__default.useRef(null);
  const selectedColor = checked ? cssVar.secondaryMain : cssVar.inputBorder;
  const inputId = `${name}-${value}`;
  const captionId = `${inputId}-caption`;
  const labelStyles = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${selectedColor}`,
    borderRadius: 4,
    gap: 4,
    padding: "10px 16px",
    cursor: "pointer"
  };
  const headerStyles = {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  };
  const inputStyles = {
    appearance: "none",
    display: "inline-block",
    backgroundColor: checked ? selectedColor : "unset",
    border: "none",
    borderRadius: "100%",
    height: 10,
    lineHeight: 16,
    outline: `1px solid ${selectedColor}`,
    outlineOffset: 2,
    width: 10,
    margin: 0,
    cursor: "pointer"
  };
  const handleFocus = () => {
    if (labelRef.current) {
      labelRef.current.style.outline = `1px solid ${cssVar.secondaryMain}`;
    }
  };
  const handleBlur = () => {
    if (labelRef.current) {
      labelRef.current.style.outline = "none";
    }
  };
  return /* @__PURE__ */ jsxs(
    "label",
    {
      htmlFor: inputId,
      "aria-label": label,
      ref: labelRef,
      style: labelStyles,
      onMouseOver: handleFocus,
      onFocus: handleFocus,
      onMouseLeave: handleBlur,
      onBlur: handleBlur,
      children: [
        /* @__PURE__ */ jsxs("span", { style: headerStyles, children: [
          /* @__PURE__ */ jsx(Typography, { children: label }),
          /* @__PURE__ */ jsx(
            "input",
            {
              id: inputId,
              ref: inputRef,
              name,
              type: "radio",
              "aria-describedby": captionId,
              onFocus: handleFocus,
              onBlur: handleBlur,
              style: inputStyles,
              value,
              checked,
              readOnly: true
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Typography, { id: captionId, variant: "caption", style: { color: cssVar.textSecondary }, children: caption })
      ]
    }
  );
}
function RadioGroup({ defaultValue, label, name, onChange, options, style, ...rest }) {
  var _a;
  const [checkedValue, setCheckedValue] = useState(
    ((_a = options.find((v) => v.value === defaultValue)) == null ? void 0 : _a.value) || options[0].value
  );
  return /* @__PURE__ */ jsxs(
    "fieldset",
    {
      style: {
        appearance: "none",
        display: "flex",
        flexDirection: "column",
        border: "none",
        margin: "0px",
        padding: "0px",
        gap: "8px",
        ...style
      },
      onChange: (e) => {
        setCheckedValue(e.target.value);
        if (onChange) {
          onChange(e);
        }
      },
      ...rest,
      children: [
        /* @__PURE__ */ jsx(Typography, { component: "legend", style: { paddingBottom: "8px" }, variant: "subtitle1", children: label }),
        options.map((opt) => /* @__PURE__ */ jsx(
          RadioGroupOption,
          {
            name,
            value: opt.value,
            label: opt.label,
            caption: opt.caption,
            checked: opt.value === checkedValue
          },
          opt.value
        ))
      ]
    }
  );
}
function TabButton({
  active,
  onClick,
  to,
  children
}) {
  let contained;
  if (to) {
    const { pathname } = useLocation();
    contained = pathname === to;
  } else {
    contained = active;
  }
  return /* @__PURE__ */ jsx(
    Button$1,
    {
      component: to ? Link$1 : Button$1,
      disableElevation: true,
      color: "secondary",
      onClick,
      to,
      variant: contained ? "contained" : "text",
      sx: {
        backgroundColor: contained ? "secondary.main" : "common.white",
        borderRadius: 1,
        flexGrow: 0,
        flexShrink: 0,
        fontWeight: 700,
        px: 2
      },
      children
    }
  );
}
const defaultValidationMessaging = (validityState) => {
  if (validityState.valueMissing)
    return "This is required.";
  return "";
};
function TextField({ customValidationMessaging, label, placeholder, name, required, ...rest }) {
  const [helperText, setHelperText] = useState("");
  const [helperVisible, setHelperVisible] = useState(false);
  const helperRef = useRef(null);
  const labelRef = useRef(null);
  const inputId = `input-text-${name}`;
  const errorId = `${inputId}-helper`;
  const handleValid = (el) => {
    if (helperRef.current && labelRef.current) {
      labelRef.current.style.color = cssVar.textPrimary;
      helperRef.current.style.opacity = "0";
      helperRef.current.style.transform = "translateY(-1rem)";
    }
    el.style.setProperty("outline-color", cssVar.secondaryMain);
    el.style.setProperty("border-color", cssVar.inputBorder);
    setTimeout(() => {
      setHelperText("");
      setHelperVisible(false);
    }, 150);
  };
  const handleInvalid = (el) => {
    el.setCustomValidity("");
    let validationMessage = "";
    if (customValidationMessaging) {
      validationMessage = customValidationMessaging(el.validity);
    }
    if (!validationMessage) {
      validationMessage = defaultValidationMessaging(el.validity);
    }
    el.setCustomValidity(validationMessage);
    const validationError = el.validationMessage;
    setHelperText(validationError);
    setHelperVisible(true);
    if (helperRef.current && labelRef.current) {
      helperRef.current.style.opacity = "1";
      helperRef.current.style.transform = "translateY(0)";
      labelRef.current.style.color = cssVar.errorMain;
    }
    el.style.setProperty("outline-color", cssVar.errorMain);
    el.style.setProperty("border-color", cssVar.errorMain);
  };
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "8px" }, children: [
    placeholder && /* @__PURE__ */ jsx("style", { children: `
          #${inputId}::placeholder {
            color: ${cssVar.inputPlaceholder};
            font-size: 16px;
            font-height: 500;
            line-height: 20px;
          }
          #${inputId}::-webkit-input-placeholder {
            color: ${cssVar.inputPlaceholder};
            font-size: 16px;
            font-height: 500;
            line-height: 20px;
          }
          #${inputId}::-moz-placeholder {
            color: ${cssVar.inputPlaceholder};
            font-size: 16px;
            font-height: 500;
            line-height: 20px;
          }
          #${inputId}:-ms-input-placeholder {
            color: ${cssVar.inputPlaceholder};
            font-size: 16px;
            font-height: 500;
            line-height: 20px;
          }
        ` }),
    /* @__PURE__ */ jsx(Typography, { component: "label", htmlFor: inputId, elRef: labelRef, variant: "subtitle1", children: label }),
    /* @__PURE__ */ jsx(
      "input",
      {
        id: inputId,
        name,
        "aria-describedby": helperVisible ? errorId : void 0,
        required,
        style: {
          appearance: "none",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: cssVar.inputBorder,
          borderRadius: 4,
          fontSize: 16,
          padding: "12px 8px",
          outline: "none",
          outlineColor: cssVar.secondaryMain,
          outlineStyle: "solid",
          outlineWidth: 0
        },
        type: "text",
        placeholder,
        onChange: (e) => {
          e.preventDefault();
          e.currentTarget.setCustomValidity("");
          const valid = e.currentTarget.reportValidity();
          if (valid) {
            handleValid(e.currentTarget);
          } else {
            handleInvalid(e.currentTarget);
          }
        },
        onInvalid: (e) => {
          e.preventDefault();
          handleInvalid(e.currentTarget);
        },
        onMouseOver: (e) => {
          e.currentTarget.style.outlineWidth = "1px";
        },
        onMouseLeave: (e) => {
          e.currentTarget.style.outlineWidth = "0px";
        },
        onFocus: (e) => {
          e.currentTarget.style.outlineWidth = "2px";
        },
        onBlur: (e) => {
          e.currentTarget.style.outlineWidth = "0px";
        },
        ...rest
      }
    ),
    /* @__PURE__ */ jsx(
      Typography,
      {
        id: errorId,
        variant: "caption",
        elRef: helperRef,
        role: helperVisible ? "alert" : void 0,
        "aria-live": "assertive",
        style: {
          color: cssVar.errorMain,
          opacity: 0,
          transition: "opacity 0.1s ease-in, transform 0.2s ease-in-out",
          transform: "translateY(-1rem)"
        },
        children: helperText
      }
    )
  ] });
}
function CloseIcon({ colour, size }) {
  return /* @__PURE__ */ jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: "0 0 24 24", fill: "none", children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z",
      fill: colour
    }
  ) });
}
const CallMeNowSnackBar = ({
  firstName,
  mobileNumber,
  error,
  open,
  activeBrand = BRAND_KEYS.ATHENA,
  successText = "",
  errorText = "",
  onClose
}) => {
  const ref = useRef(null);
  const [opened, setOpen] = useState(open);
  const [animated, setAnimated] = useState(false);
  const { isMobile } = useWindowSize();
  const [width, setWidth] = useState();
  useEffect(() => {
    var _a;
    setWidth((_a = ref.current) == null ? void 0 : _a.offsetWidth);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      });
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);
  const snackBarStyle = {
    paddingLeft: 16,
    paddingRight: 16,
    ...getThemeStyles({ activeBrand }),
    // Position styles
    position: "fixed",
    bottom: "8%",
    ...width === void 0 ? {} : {
      left: isMobile ? 0 : `calc(50% - ${width / 2}px)`,
      right: isMobile ? 0 : "unset"
    },
    zIndex: 9999,
    // Animation styles
    transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
    opacity: 0,
    transform: "translateY(40px)"
  };
  const defaultErrorText = errorText || "Sorry, something went wrong. Try again later or contact us.";
  const defaultSuccessText = successText || `Thanks ${firstName}, we'll be in touch on ${mobileNumber} as soon as possible.`;
  const activeText = error ? defaultErrorText : defaultSuccessText;
  return opened ? /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      style: animated ? {
        ...snackBarStyle,
        ...{
          opacity: 1,
          transform: "translateY(0)"
        }
      } : snackBarStyle,
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            fontFamily: "system-ui",
            fontSize: 16,
            lineHeight: 1.6,
            color: error ? cssVar.snackBarErrorText : cssVar.snackBarInfoText,
            backgroundColor: error ? cssVar.snackBarErrorBackground : cssVar.snackBarInfoBackground,
            borderRadius: 4
          },
          children: [
            /* @__PURE__ */ jsx("div", { style: { paddingLeft: 12, paddingTop: 12, paddingBottom: 12, flex: 1 }, children: activeText }),
            /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                style: {
                  all: "unset",
                  cursor: "pointer",
                  height: 48,
                  width: 48,
                  boxSizing: "border-box",
                  paddingTop: 4,
                  textAlign: "center"
                },
                onClick: () => {
                  onClose && onClose();
                  setOpen(false);
                },
                "aria-label": "close",
                children: /* @__PURE__ */ jsx(CloseIcon, { colour: error ? cssVar.snackBarErrorText : cssVar.snackBarInfoText, size: 24 })
              }
            ) })
          ]
        }
      )
    }
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    /* @__PURE__ */ jsx(Fragment, {})
  );
};
const CalendlyPopup = ({
  open,
  onClose,
  url,
  utm,
  prefill
}) => {
  const [rootElement, setRootElement] = useState();
  useEffect(() => {
    setRootElement(document.body);
  }, []);
  return rootElement ? /* @__PURE__ */ jsx(PopupModal, { rootElement, url, utm, prefill, open, onModalClose: onClose }) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    /* @__PURE__ */ jsx(Fragment, {})
  );
};
const AUSTRALIAN_MOBILE_REGEX = /(\+61|0)4(\d[\s\-]*){8}/;
const useDialogState = () => {
  const [isOpen, setIsOpen] = useState(false);
  const openDialog = () => {
    setIsOpen(true);
  };
  const closeDialog = () => {
    setIsOpen(false);
  };
  return { isOpen, openDialog, closeDialog };
};
export {
  AUSTRALIAN_MOBILE_REGEX,
  ArrowForward,
  Button,
  CalendlyDialog,
  CalendlyPopup,
  CallMeNowSnackBar,
  Close,
  MUILessContactUs as ContactUs,
  Dialog,
  FaviconChanger,
  MUILessFooter as Footer,
  MUILessHeader as Header,
  Home,
  Link,
  LoadingScreen,
  Logout,
  MaxWidthContainer,
  Person,
  Phone,
  MUILessProfileOptions as ProfileOptions,
  RadioGroup,
  RadioGroupOption,
  Refresh,
  TabButton,
  TextField,
  Typography,
  contactDetails,
  cssVar,
  getSharedBreakpoints,
  getThemeStyles,
  themes,
  useBrandContact,
  useDialogState,
  useWindowSize
};
